import React, { useEffect, useState } from 'react';
import api from '../api';
import { BASE_URL } from '../config';
import { Play, Activity, MessageSquare, Users, Loader2, ShoppingBag, ShoppingCart, Package, Link, Send, Store, Handshake, Save, FileText, Trash2, Calendar, Clock, Repeat, Pause, RefreshCw, RotateCw, X, Edit3, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useConfirm } from '../context/ConfirmContext';
import TikTokOfferBank from '../components/TikTokOfferBank';

const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:') || mediaPath.startsWith('data:')) {
        return mediaPath;
    }
    const cleanBase = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
    const cleanPath = mediaPath.startsWith('/') ? mediaPath : `/${mediaPath}`;
    return `${cleanBase}${cleanPath}`;
};

const WhatsappIcon = ({ size, color }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
);

const TelegramIcon = ({ size, color }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="m20.665 3.717-17.73 6.837c-1.21.486-1.203 1.161-.222 1.462l4.552 1.42 10.532-6.645c.498-.303.953-.14.579.192l-8.533 7.701h-.002l.002.001-.314 4.692c.46 0 .663-.211.921-.46l2.211-2.15 4.599 3.397c.848.467 1.457.227 1.668-.785l3.019-14.228c.309-1.239-.473-1.8-1.282-1.434z" />
    </svg>
);

const InstagramIcon = ({ size, color }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
    </svg>
);

const TikTokIcon = ({ size = 20, color = 'currentColor' }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3 15.28a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.71a8.21 8.21 0 0 0 4.91 1.62v-3.5a4.87 4.87 0 0 1-1-.14z"/>
    </svg>
);

const Dashboard = ({ userPlan = 'free', userRole = 'user', onNavigateTab }) => {
    const confirm = useConfirm();
    const isAdmin = userRole === 'admin' || userRole === 'moderator';
    const [serverOnline, setServerOnline] = useState(false);
    const [whatsConnected, setWhatsConnected] = useState(false);
    const [statsData, setStatsData] = useState({ postagens: 0, grupos: 0 });
    const [plataformasAtivas, setPlataformasAtivas] = useState([]);
    const [manualLink, setManualLink] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [postarTelegram, setPostarTelegram] = useState(true);
    const [postarWhatsapp, setPostarWhatsapp] = useState(true);
    const [postarStatus, setPostarStatus] = useState(false);
    const [postarInstagram, setPostarInstagram] = useState(true);
    const [postarInstagramType, setPostarInstagramType] = useState('both');
    const [dynamicCooldownSecs, setDynamicCooldownSecs] = useState(3600); // 1h
    const [userConfig, setUserConfig] = useState(null);

    // Estados da Programação de Horários & Modo Automático (Afiliado)
    const [schedAutoPostStatus, setSchedAutoPostStatus] = useState(true);
    const [schedHoraInicio, setSchedHoraInicio] = useState(8);
    const [schedHoraFim, setSchedHoraFim] = useState(22);
    const [schedIntervaloMinutos, setSchedIntervaloMinutos] = useState(30);
    const [schedDiasExecucao, setSchedDiasExecucao] = useState('0,1,2,3,4,5,6');
    const [schedPlataformasAutoPost, setSchedPlataformasAutoPost] = useState('shopee,ml,amazon,magalu,shein,aliexpress');
    const [schedInstagramHoraInicio, setSchedInstagramHoraInicio] = useState(8);
    const [schedInstagramHoraFim, setSchedInstagramHoraFim] = useState(22);
    const [schedAutoPostWhatsapp, setSchedAutoPostWhatsapp] = useState(true);
    const [schedAutoPostWhatsappStatus, setSchedAutoPostWhatsappStatus] = useState(false);
    const [schedAutoPostInstagramStories, setSchedAutoPostInstagramStories] = useState(false);
    const [schedAutoPostInstagramFeed, setSchedAutoPostInstagramFeed] = useState(false);
    const [schedAutoPostTelegram, setSchedAutoPostTelegram] = useState(false);
    const [savingAffiliateSchedule, setSavingAffiliateSchedule] = useState(false);

    const toggleSchedDay = (id) => {
        let dias = schedDiasExecucao ? schedDiasExecucao.split(',').map(d => parseInt(d.trim(), 10)).filter(n => !isNaN(n)) : [];
        if (dias.includes(id)) {
            dias = dias.filter(d => d !== id);
        } else {
            dias.push(id);
        }
        setSchedDiasExecucao(dias.sort((a, b) => a - b).join(','));
    };

    const setSchedDaysPreset = (preset) => {
        if (preset === 'all') setSchedDiasExecucao('0,1,2,3,4,5,6');
        else if (preset === 'weekdays') setSchedDiasExecucao('1,2,3,4,5');
        else if (preset === 'weekend') setSchedDiasExecucao('0,6');
    };

    const toggleSchedPlat = (id) => {
        let plats = schedPlataformasAutoPost ? schedPlataformasAutoPost.split(',').map(p => p.trim().toLowerCase()).filter(Boolean) : [];
        if (plats.includes(id)) {
            if (plats.length > 1) {
                plats = plats.filter(p => p !== id);
            } else {
                toast.error("Mantenha ao menos 1 plataforma selecionada!");
                return;
            }
        } else {
            plats.push(id);
        }
        setSchedPlataformasAutoPost(plats.join(','));
    };

    const handleSaveAffiliateSchedule = async () => {
        setSavingAffiliateSchedule(true);
        const toastId = toast.loading("Salvando programação de horários...");
        try {
            const payload = {
                autoPostStatus: schedAutoPostStatus,
                horaInicio: parseInt(schedHoraInicio, 10) || 8,
                horaFim: parseInt(schedHoraFim, 10) || 22,
                intervaloMinutos: parseInt(schedIntervaloMinutos, 10) || 30,
                diasExecucao: schedDiasExecucao || '0,1,2,3,4,5,6',
                plataformasAutoPost: schedPlataformasAutoPost,
                instagramHoraInicio: parseInt(schedInstagramHoraInicio, 10) || 8,
                instagramHoraFim: parseInt(schedInstagramHoraFim, 10) || 22,
                autoPostWhatsapp: schedAutoPostWhatsapp,
                autoPostWhatsappStatus: schedAutoPostWhatsappStatus,
                autoPostInstagramStories: schedAutoPostInstagramStories,
                autoPostInstagramFeed: schedAutoPostInstagramFeed,
                autoPostTelegram: schedAutoPostTelegram
            };
            await api.post('/config', payload);
            setUserConfig(prev => ({ ...prev, ...payload }));
            toast.success("Programação de horários salva com sucesso!", { id: toastId });
        } catch (err) {
            console.error("Erro ao salvar programação:", err);
            toast.error("Erro ao salvar programação.", { id: toastId });
        } finally {
            setSavingAffiliateSchedule(false);
        }
    };

    const handleToggleAutoPostStatusImmediate = async () => {
        const nextVal = !schedAutoPostStatus;
        setSchedAutoPostStatus(nextVal);
        try {
            await api.post('/config', { autoPostStatus: nextVal });
            setUserConfig(prev => ({ ...prev, autoPostStatus: nextVal }));
            toast.success(nextVal ? "🟢 Modo Automático ATIVADO!" : "⏸️ Modo Automático PAUSADO!");
        } catch (err) {
            setSchedAutoPostStatus(!nextVal);
            toast.error("Erro ao alternar modo automático.");
        }
    };

    const togglePlataforma = async (id) => {
        let newPlats;
        if (plataformasAtivas.includes(id)) {
            if (plataformasAtivas.length > 1) {
                newPlats = plataformasAtivas.filter(p => p !== id);
            } else {
                newPlats = plataformasAtivas;
            }
        } else {
            newPlats = [...plataformasAtivas, id];
        }
        setPlataformasAtivas(newPlats);
        try {
            await api.post('/config', { manualPostPlatforms: newPlats.join(',') });
        } catch (e) {
            console.error("Erro ao salvar manualPostPlatforms:", e.message);
        }
    };

    const handlePostarInstagramTypeChange = async (val) => {
        setPostarInstagramType(val);
        try {
            await api.post('/config', { instagramManualPostType: val });
            setUserConfig(prev => prev ? { ...prev, instagramManualPostType: val } : null);
        } catch (err) {
            console.error("Erro ao salvar tipo de postagem manual do Instagram:", err.message);
            toast.error("Erro ao salvar preferência de postagem no Instagram.");
        }
    };

    const handlePostarStatusChange = async (val) => {
        setPostarStatus(val);
        try {
            await api.post('/config', { whatsappStatusActive: val });
            setUserConfig(prev => prev ? { ...prev, whatsappStatusActive: val } : null);
        } catch (err) {
            console.error("Erro ao salvar status do Whatsapp:", err.message);
            toast.error("Erro ao salvar preferência de postagem no Status.");
        }
    };

    const handleToggleWhatsapp = async () => {
        const newVal = !postarWhatsapp;
        setPostarWhatsapp(newVal);
        try {
            await api.post('/config', { manualPostWhatsapp: newVal });
        } catch (e) {
            console.error("Erro ao salvar manualPostWhatsapp:", e.message);
        }
    };

    const handleToggleTelegram = async () => {
        const newVal = !postarTelegram;
        setPostarTelegram(newVal);
        try {
            await api.post('/config', { manualPostTelegram: newVal });
        } catch (e) {
            console.error("Erro ao salvar manualPostTelegram:", e.message);
        }
    };

    const handleToggleInstagram = async () => {
        const newVal = !postarInstagram;
        setPostarInstagram(newVal);
        try {
            await api.post('/config', { manualPostInstagram: newVal });
        } catch (e) {
            console.error("Erro ao salvar manualPostInstagram:", e.message);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const endpoint = (userRole === 'admin' || userRole === 'moderator') 
                    ? '/config/whatsapp/status/B' 
                    : '/config/whatsapp/me';
                const resStatus = await api.get(endpoint);
                setWhatsConnected(resStatus.data.connected);
                setServerOnline(true);
            } catch (err) {
                console.log("Servidor offline ou erro ao obter status:", err.message);
                setWhatsConnected(false);
                setServerOnline(false);
            }

            if (userRole === 'admin' || userRole === 'moderator') {
                try {
                    const resStats = await api.get('/bot/stats');
                    setStatsData({
                        postagens: resStats.data.postagens,
                        grupos: resStats.data.grupos
                    });
                } catch (err) {
                    console.log("Erro ao buscar estatísticas do bot:", err.message);
                }
            }

            try {
                const resConfig = await api.get('/config');
                setUserConfig(resConfig.data);
                if (resConfig.data.autoPostStatus !== undefined) {
                    setSchedAutoPostStatus(resConfig.data.autoPostStatus);
                }
                if (resConfig.data.horaInicio !== undefined) {
                    setSchedHoraInicio(resConfig.data.horaInicio);
                }
                if (resConfig.data.horaFim !== undefined) {
                    setSchedHoraFim(resConfig.data.horaFim);
                }
                if (resConfig.data.intervaloMinutos !== undefined) {
                    setSchedIntervaloMinutos(resConfig.data.intervaloMinutos);
                }
                if (resConfig.data.diasExecucao !== undefined) {
                    setSchedDiasExecucao(resConfig.data.diasExecucao || '0,1,2,3,4,5,6');
                }
                if (resConfig.data.plataformasAutoPost !== undefined) {
                    setSchedPlataformasAutoPost(resConfig.data.plataformasAutoPost || 'shopee,ml,amazon,magalu,shein,aliexpress');
                }
                if (resConfig.data.instagramHoraInicio !== undefined) {
                    setSchedInstagramHoraInicio(resConfig.data.instagramHoraInicio);
                }
                if (resConfig.data.instagramHoraFim !== undefined) {
                    setSchedInstagramHoraFim(resConfig.data.instagramHoraFim);
                }
                if (resConfig.data.autoPostWhatsapp !== undefined) {
                    setSchedAutoPostWhatsapp(resConfig.data.autoPostWhatsapp);
                }
                if (resConfig.data.autoPostWhatsappStatus !== undefined) {
                    setSchedAutoPostWhatsappStatus(resConfig.data.autoPostWhatsappStatus);
                }
                if (resConfig.data.autoPostInstagramStories !== undefined) {
                    setSchedAutoPostInstagramStories(resConfig.data.autoPostInstagramStories);
                }
                if (resConfig.data.autoPostInstagramFeed !== undefined) {
                    setSchedAutoPostInstagramFeed(resConfig.data.autoPostInstagramFeed);
                }
                if (resConfig.data.autoPostTelegram !== undefined) {
                    setSchedAutoPostTelegram(resConfig.data.autoPostTelegram);
                }
                if (resConfig.data.instagramManualPostType) {
                    setPostarInstagramType(resConfig.data.instagramManualPostType);
                }
                if (resConfig.data.whatsappStatusActive !== undefined) {
                    setPostarStatus(resConfig.data.whatsappStatusActive);
                }
                if (resConfig.data.manualPostWhatsapp !== undefined) {
                    setPostarWhatsapp(resConfig.data.manualPostWhatsapp);
                }
                if (resConfig.data.manualPostTelegram !== undefined) {
                    setPostarTelegram(resConfig.data.manualPostTelegram);
                }
                if (resConfig.data.manualPostInstagram !== undefined) {
                    setPostarInstagram(resConfig.data.manualPostInstagram);
                }
                if (resConfig.data.manualPostPlatforms) {
                    setPlataformasAtivas(resConfig.data.manualPostPlatforms.split(',').map(p => p.trim()));
                } else {
                    const plats = [];
                    if (resConfig.data.shopeeAppId) plats.push('shopee');
                    if (resConfig.data.mlTag) plats.push('ml');
                    if (resConfig.data.amazonTag) plats.push('amazon');
                    if (resConfig.data.magaluTag) plats.push('magalu');
                    if (resConfig.data.sheinUid) plats.push('shein');
                    if (resConfig.data.aliexpressAppKey) plats.push('aliexpress');
                    if (resConfig.data.tiktokCookie || resConfig.data.tiktokTag || resConfig.data.tiktokAccessToken || resConfig.data.tiktokPublisherId) plats.push('tiktok');
                    setPlataformasAtivas(plats);
                }
            } catch (err) {
                console.log("Erro ao buscar configurações:", err.message);
            }
        };

        fetchData();

        // Polling para checar status do bot em background
        const checkStatus = async () => {
            try {
                const res = await api.get('/bot/status');
                setIsProcessing(res.data.isProcessing);
                if (res.data.cooldownDisparoFreeSegundos) {
                    setDynamicCooldownSecs(res.data.cooldownDisparoFreeSegundos);
                }
                setServerOnline(true);
            } catch (err) {
                console.log("Erro ao checar status");
                setServerOnline(false);
            }
        };

        checkStatus();
        const interval = setInterval(checkStatus, 3000);
        return () => clearInterval(interval);
    }, [userRole]);

    const handleDisparoManual = async () => {
        if (userPlan === 'free' || userPlan === 'free_divulgador') {
            return toast.error("🔒 Disparo automático de promoções é exclusivo para planos pagos. Faça o upgrade para o Plano Básico!");
        }
        const canInstagram = (userPlan === 'premium' || userPlan === 'premium_divulgador' || userRole === 'admin') && !!userConfig?.instagramBusinessAccountId;
        const wantsWhatsapp = postarWhatsapp && whatsConnected;
        const wantsTelegram = postarTelegram;
        const wantsInstagram = postarInstagram && canInstagram;
        const wantsStatus = postarStatus && whatsConnected;

        if (!wantsWhatsapp && !wantsTelegram && !wantsInstagram && !wantsStatus) {
            return toast.error("Selecione pelo menos um canal de envio (WhatsApp conectado, Status, Telegram ou Instagram)!");
        }

        if (wantsWhatsapp && !whatsConnected) {
            return toast.error("WhatsApp está offline! Sincronize seu WhatsApp pessoal nas Configurações ou desative 'Publicar no Whatsapp' para enviar nos outros canais.");
        }
        if (isProcessing) return toast.error("Já existe um disparo em andamento!");

        const idCarregamento = toast.loading(`Iniciando disparo: ${plataformasAtivas.join(' + ').toUpperCase()}...`);

        try {
            setIsProcessing(true);
            const res = await api.post('/bot/enviar-manual', {
                plataformas: plataformasAtivas,
                postarInstagram: postarInstagram && canInstagram,
                postarInstagramType: postarInstagramType,
                postarWhatsapp: wantsWhatsapp,
                postarTelegram: postarTelegram,
                postarStatus: wantsStatus
            });
            toast.success(res.data.message, { id: idCarregamento });

            if (userRole === 'admin' || userRole === 'moderator') {
                const s = await api.get('/bot/stats');
                setStatsData(s.data);
            }
        } catch (err) {
            setIsProcessing(false);
            const errorMsg = err.response?.data?.error || err.response?.data?.message || "Erro ao iniciar disparo";
            toast.error(errorMsg, { id: idCarregamento });
        }
    };

    const handleSendManualLink = async () => {
        if (userPlan === 'free' || userPlan === 'free_divulgador') {
            const hasWhatsapp = (userConfig?.userWhatsappVerified && userConfig?.userWhatsappNumber) || whatsConnected;
            const hasTelegram = !!userConfig?.telegramToken;
            if (!hasWhatsapp && !hasTelegram) {
                return toast.error("⚠️ Nenhum canal de envio configurado. Conecte seu WhatsApp ou configure seu Telegram nas Configurações!");
            }
            if (cooldownTimeRemaining) {
                return toast.error(`🔒 Limite do Plano Free: Aguarde ${cooldownTimeRemaining} para o próximo envio!`);
            }
            const activePlat = userConfig?.activePlatform;
            const isMagalu = manualLink.includes('magazineluiza.com.br') || manualLink.includes('magazinevoce.com.br') || manualLink.includes('magalu');
            const isShopee = manualLink.includes('shopee.com') || manualLink.includes('shope.ee') || manualLink.includes('shp.ee');

            if (activePlat === 'magalu' && !isMagalu) {
                return toast.error("🔒 Sua plataforma ativa é Magalu. O Plano Gratuito suporta apenas o envio de links da sua plataforma ativa!");
            } else if (activePlat === 'shopee' && !isShopee) {
                return toast.error("🔒 Sua plataforma ativa é Shopee. O Plano Gratuito suporta apenas o envio de links da sua plataforma ativa!");
            } else if (activePlat !== 'magalu' && activePlat !== 'shopee') {
                return toast.error("🔒 Você precisa configurar a Shopee ou Magazine Luiza como sua plataforma ativa nas Configurações para enviar links.");
            }
        } else {
            const canInstagram = userPlan === 'premium' || userPlan === 'premium_divulgador' || userRole === 'admin';
            const wantsWhatsapp = postarWhatsapp && whatsConnected;
            const wantsTelegram = postarTelegram;
            const wantsInstagram = postarInstagram && canInstagram;
            const wantsStatus = postarStatus && whatsConnected;

            if (!wantsWhatsapp && !wantsTelegram && !wantsInstagram && !wantsStatus) {
                return toast.error("Selecione pelo menos um canal de envio (WhatsApp conectado, Status, Telegram ou Instagram)!");
            }

            if (wantsWhatsapp && !whatsConnected) {
                return toast.error("WhatsApp está offline! Sincronize seu WhatsApp pessoal nas Configurações ou desative 'Publicar no Whatsapp' para enviar nos outros canais.");
            }
        }

        if (!manualLink) return toast.error("Insira o link do produto!");
        if (isProcessing) return toast.error("Já existe um disparo em andamento!");

        const idCarregamento = toast.loading("Iniciando envio...");
        try {
            setIsProcessing(true);
            let res;
            if (userPlan === 'free' || userPlan === 'free_divulgador') {
                res = await api.post('/bot/enviar-link-manual-free', {
                    link: manualLink,
                    template: messageTemplate
                });
            } else {
                res = await api.post('/bot/enviar-link-manual', {
                    link: manualLink,
                    postarInstagram: postarInstagram && (userPlan === 'premium' || userPlan === 'premium_divulgador' || userRole === 'admin'),
                    postarInstagramType: postarInstagramType,
                    postarWhatsapp: postarWhatsapp && whatsConnected,
                    postarTelegram: postarTelegram,
                    postarStatus: postarStatus && whatsConnected
                });
            }

            toast.success(res.data.message, { id: idCarregamento });
            setManualLink("");

            if (userPlan === 'free' || userPlan === 'free_divulgador') {
                const resConfig = await api.get('/config');
                setUserConfig(resConfig.data);
            }

            if (userRole === 'admin' || userRole === 'moderator') {
                const s = await api.get('/bot/stats');
                setStatsData(s.data);
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || "Erro ao iniciar link", { id: idCarregamento });
        } finally {
            setIsProcessing(false);
        }
    };

    const isUserAdmin = userRole === 'admin' || userRole === 'moderator';
    const stats = [
        {
            label: 'Server',
            value: serverOnline ? 'Online' : 'Offline',
            icon: <Activity color={serverOnline ? "#22c55e" : "#ef4444"} />,
            color: serverOnline ? '#22c55e' : '#ef4444'
        },
        ...(isUserAdmin ? [
            {
                label: 'Postagens Hoje',
                value: statsData.postagens,
                icon: <MessageSquare color="#3b82f6" />,
                color: '#3b82f6'
            },
            {
                label: 'Grupos Ativos',
                value: statsData.grupos,
                icon: <Users color="#f59e0b" />,
                color: '#f59e0b'
            }
        ] : [])
    ];

    const [freeLinkInput, setFreeLinkInput] = useState("");
    const [convertedLink, setConvertedLink] = useState("");
    const [convertedProduct, setConvertedProduct] = useState(null);
    const [convertingFree, setConvertingFree] = useState(false);
    const [messageTemplate, setMessageTemplate] = useState(
        `📦 *{nome}*\n\n🛍️ *OFERTA!*\n❌ *DE: R$ {precoAntigo}*\n💰 *POR: R$ {preco}*\n\n🛒 *LINK:* {link}\n\n🏪 Loja: *{loja}*`
    );

    const [sendingShopeeFree, setSendingShopeeFree] = useState(false);
    const [cooldownTimeRemaining, setCooldownTimeRemaining] = useState("");
    const [freeTab, setFreeTab] = useState("shopee"); // "shopee" or "manual"
    const [mainTab, setMainTab] = useState((isAdmin || (userPlan && userPlan.endsWith('_divulgador'))) ? 'divulgador' : 'afiliado'); // "afiliado" or "divulgador"
    const [divulgadorTexto, setDivulgadorTexto] = useState('');
    const [divulgadorMedia, setDivulgadorMedia] = useState(null);
    const [sendingDivulgador, setSendingDivulgador] = useState(false);
    
    // Configurações de Agendamento do Divulgador
    const [divulgadorScheduleType, setDivulgadorScheduleType] = useState('now'); // 'now', 'once', 'recurring'
    const [divulgadorScheduledAt, setDivulgadorScheduledAt] = useState('');
    const [divulgadorRecurringDays, setDivulgadorRecurringDays] = useState([0, 1, 2, 3, 4, 5, 6]);
    const [divulgadorRecurringMode, setDivulgadorRecurringMode] = useState('fixed_times'); // 'fixed_times' | 'interval'
    const [divulgadorRecurringTimes, setDivulgadorRecurringTimes] = useState('09:00');
    const [divulgadorIntervalMinutes, setDivulgadorIntervalMinutes] = useState(60);
    const [divulgadorIntervalStart, setDivulgadorIntervalStart] = useState('08:00');
    const [divulgadorIntervalEnd, setDivulgadorIntervalEnd] = useState('22:00');
    const [divulgadorPostarWa, setDivulgadorPostarWa] = useState(true);
    const [divulgadorPostarStatus, setDivulgadorPostarStatus] = useState(false);
    const [divulgadorPostarTg, setDivulgadorPostarTg] = useState(true);
    const [divulgadorPostarIg, setDivulgadorPostarIg] = useState(false);
    const [divulgadorPostarIgType, setDivulgadorPostarIgType] = useState('both'); // 'both', 'feed', 'story'

    // Saved Posts States
    const [savedPosts, setSavedPosts] = useState([]);
    const [savedPostMediaPath, setSavedPostMediaPath] = useState(null);
    const [isSavingPost, setIsSavingPost] = useState(false);
    const [savedPostTitle, setSavedPostTitle] = useState("");
    const [isSavedPostsLoading, setIsSavedPostsLoading] = useState(false);
    const [quickSendingId, setQuickSendingId] = useState(null);

    // Modal de Edição de Agendamento
    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [selectedPostForSchedule, setSelectedPostForSchedule] = useState(null);
    const [modalScheduleType, setModalScheduleType] = useState('now');
    const [modalScheduledAt, setModalScheduledAt] = useState('');
    const [modalRecurringDays, setModalRecurringDays] = useState([0, 1, 2, 3, 4, 5, 6]);
    const [modalRecurringMode, setModalRecurringMode] = useState('fixed_times');
    const [modalRecurringTimes, setModalRecurringTimes] = useState('09:00');
    const [modalIntervalMinutes, setModalIntervalMinutes] = useState(60);
    const [modalIntervalStart, setModalIntervalStart] = useState('08:00');
    const [modalIntervalEnd, setModalIntervalEnd] = useState('22:00');
    const [modalPostarWa, setModalPostarWa] = useState(true);
    const [modalPostarStatus, setModalPostarStatus] = useState(false);
    const [modalPostarTg, setModalPostarTg] = useState(true);
    const [modalPostarIg, setModalPostarIg] = useState(false);
    const [modalPostarIgType, setModalPostarIgType] = useState('both');
    const [modalStatus, setModalStatus] = useState('active');
    const [isSavingScheduleModal, setIsSavingScheduleModal] = useState(false);

    const DIAS_SEMANA_LIST = [
        { id: 0, nome: 'Dom' },
        { id: 1, nome: 'Seg' },
        { id: 2, nome: 'Ter' },
        { id: 3, nome: 'Qua' },
        { id: 4, nome: 'Qui' },
        { id: 5, nome: 'Sex' },
        { id: 6, nome: 'Sáb' }
    ];

    const formatDateTimeBR = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const formatRecurringDays = (daysStr) => {
        if (!daysStr) return 'Todos os dias';
        const arr = daysStr.split(',').map(d => parseInt(d.trim(), 10)).filter(n => !isNaN(n));
        if (arr.length === 7) return 'Todos os dias';
        if (arr.length === 5 && [1, 2, 3, 4, 5].every(d => arr.includes(d))) return 'Seg a Sex';
        if (arr.length === 2 && [0, 6].every(d => arr.includes(d))) return 'Fins de Semana';
        const nomes = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
        return arr.sort((a, b) => a - b).map(d => nomes[d]).join(', ');
    };

    const formatIntervalText = (minutes) => {
        const min = parseInt(minutes, 10) || 60;
        if (min < 60) return `${min} min`;
        const hours = Math.floor(min / 60);
        const restMin = min % 60;
        if (restMin === 0) return hours === 1 ? '1 hora' : `${hours} horas`;
        return `${hours}h ${restMin}min`;
    };

    const INTERVALO_OPCOES = [
        { label: 'A cada 15 min', value: 15 },
        { label: 'A cada 30 min', value: 30 },
        { label: 'A cada 45 min', value: 45 },
        { label: 'A cada 1 hora', value: 60 },
        { label: 'A cada 1h e 30m', value: 90 },
        { label: 'A cada 2 horas', value: 120 },
        { label: 'A cada 3 horas', value: 180 },
        { label: 'A cada 4 horas', value: 240 },
        { label: 'A cada 6 horas', value: 360 },
        { label: 'A cada 8 horas', value: 480 },
        { label: 'A cada 12 horas', value: 720 }
    ];

    useEffect(() => {
        if (isAdmin || (userPlan && userPlan.endsWith('_divulgador'))) {
            setMainTab('divulgador');
        } else {
            setMainTab('afiliado');
        }
    }, [isAdmin, userPlan]);

    useEffect(() => {
        if (userPlan === 'free' || userPlan === 'free_divulgador') {
            if (userConfig?.activePlatform) {
                if (userConfig.activePlatform === 'magalu') {
                    setFreeTab("manual");
                } else {
                    setFreeTab("shopee");
                }

                const saved = localStorage.getItem('free_msg_template');
                if (saved) {
                    setMessageTemplate(saved);
                } else {
                    const activePlat = userConfig.activePlatform;
                    const defaultTmpl = activePlat === 'magalu'
                        ? `📦 *{nome}*\n\n🛍️ *OFERTA MAGALU!*\n❌ *DE: R$ {precoAntigo}*\n💰 *POR: R$ {preco}*\n\n🛒 *LINK:* {link}\n\n🏪 Loja: *{loja}*`
                        : `📦 *{nome}*\n\n🛍️ *OFERTA SHOPEE!*\n❌ *DE: R$ {precoAntigo}*\n💰 *POR: R$ {preco}*\n\n🛒 *LINK:* {link}\n\n🏪 Loja: *{loja}*`;
                    setMessageTemplate(defaultTmpl);
                }
            }
        } else {
            const saved = localStorage.getItem('non_free_msg_template');
            if (saved) {
                setMessageTemplate(saved);
            } else if (userConfig?.templateMensagem) {
                setMessageTemplate(userConfig.templateMensagem);
            }
        }
    }, [userPlan, userConfig?.activePlatform, userConfig?.templateMensagem]);

    useEffect(() => {
        if ((userPlan === 'free' || userPlan === 'free_divulgador') && userConfig?.lastFreePostAt) {
            const updateTimer = () => {
                const lastPost = new Date(userConfig.lastFreePostAt).getTime();
                const now = new Date().getTime();
                const cooldown = dynamicCooldownSecs * 1000;
                const diff = lastPost + cooldown - now;
                if (diff <= 0) {
                    setCooldownTimeRemaining("");
                } else {
                    const hrs = Math.floor(diff / (1000 * 60 * 60));
                    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                    const secs = Math.floor((diff % (1000 * 60)) / 1000);
                    setCooldownTimeRemaining(`${hrs}h ${mins}m ${secs}s`);
                }
            };
            updateTimer();
            const interval = setInterval(updateTimer, 1000);
            return () => clearInterval(interval);
        }
    }, [userPlan, userConfig?.lastFreePostAt, dynamicCooldownSecs]);

    const [activeGroupsCount, setActiveGroupsCount] = useState(null);

    const fetchSavedPosts = async () => {
        try {
            setIsSavedPostsLoading(true);
            const [resPosts, resGroups] = await Promise.allSettled([
                api.get('/saved-posts'),
                api.get('/grupos')
            ]);
            if (resPosts.status === 'fulfilled') {
                setSavedPosts(resPosts.value.data);
            }
            if (resGroups.status === 'fulfilled') {
                const count = (resGroups.value.data || []).filter(g => g.ativo).length;
                setActiveGroupsCount(count);
            }
        } catch (error) {
            console.error("Erro ao buscar publicações salvas:", error);
        } finally {
            setIsSavedPostsLoading(false);
        }
    };

    useEffect(() => {
        if (mainTab === 'divulgador') {
            fetchSavedPosts();
        }
    }, [mainTab]);

    const handleFreeConvert = async () => {
        if (!freeLinkInput) return toast.error("Por favor, cole um link!");
        setConvertingFree(true);
        const loadToast = toast.loading("Processando e convertendo link...");

        try {
            const res = await api.post('/bot/convert-link', { link: freeLinkInput });
            if (res.data && res.data.success) {
                setConvertedLink(res.data.offerLink);
                setConvertedProduct(res.data);
                toast.success("Link convertido com sucesso!", { id: loadToast });
            } else {
                throw new Error("Erro na conversão.");
            }
        } catch (err) {
            console.error("Erro na conversão:", err);
            const errMsg = err.response?.data?.error || "Erro ao processar o link. Verifique se ele é válido.";
            toast.error(errMsg, { id: loadToast });
        } finally {
            setConvertingFree(false);
        }
    };

    const formatPreviewMessage = () => {
        if (!convertedProduct) return "";
        return messageTemplate
            .replace(/{nome}/g, (convertedProduct.productName || "Produto").toUpperCase())
            .replace(/{precoAntigo}/g, convertedProduct.oldPrice || "")
            .replace(/{preco}/g, convertedProduct.price || "")
            .replace(/{link}/g, convertedLink || "")
            .replace(/{loja}/g, (convertedProduct.loja || "MAGAZINE LUIZA").toUpperCase());
    };

    const handleCopyFormattedMessage = () => {
        const formatted = formatPreviewMessage();
        navigator.clipboard.writeText(formatted);
        toast.success("Mensagem formatada copiada!");
    };

    const handleDispararShopeeFree = async () => {
        const hasWhatsapp = (userConfig?.userWhatsappVerified && userConfig?.userWhatsappNumber) || whatsConnected;
        const hasTelegram = !!userConfig?.telegramToken;
        if (!hasWhatsapp && !hasTelegram) {
            return toast.error("⚠️ Nenhum canal de envio configurado. Conecte seu WhatsApp ou configure seu Telegram nas Configurações!");
        }
        if (cooldownTimeRemaining) {
            return toast.error(`🔒 Aguarde ${cooldownTimeRemaining} para o próximo disparo!`);
        }

        setSendingShopeeFree(true);
        const loadToast = toast.loading("Buscando e disparando achadinho do dia...");

        try {
            const res = await api.post('/bot/disparar-shopee-free', {
                template: messageTemplate
            });

            if (res.data && res.data.success) {
                toast.success(`🚀 Oferta enviada com sucesso! [${res.data.productName}]`, { id: loadToast });
                // Atualiza config do usuário para pegar nova data
                const resConfig = await api.get('/config');
                setUserConfig(resConfig.data);
            } else {
                throw new Error("Erro no envio.");
            }
        } catch (err) {
            console.error("Erro ao disparar Shopee Free:", err);
            const errMsg = err.response?.data?.error || "Erro ao processar o disparo Shopee do dia.";
            toast.error(errMsg, { id: loadToast });
        } finally {
            setSendingShopeeFree(false);
        }
    };

    const handleSendDivulgador = async () => {
        const canInstagram = (userPlan === 'premium' || userPlan === 'premium_divulgador' || userRole === 'admin') && !!userConfig?.instagramBusinessAccountId;
        const wantsWhatsapp = divulgadorPostarWa && whatsConnected;
        const wantsTelegram = divulgadorPostarTg;
        const wantsInstagram = divulgadorPostarIg && canInstagram;
        const wantsStatus = divulgadorPostarStatus && whatsConnected;

        if (userPlan === 'free' || userPlan === 'free_divulgador') {
            const hasWhatsapp = userConfig?.userWhatsappVerified && userConfig?.userWhatsappNumber;
            const hasTelegram = !!userConfig?.telegramToken;
            if (!hasWhatsapp && !hasTelegram && !wantsStatus) {
                return toast.error("⚠️ Nenhum canal de envio configurado.");
            }
            if (divulgadorScheduleType === 'now' && cooldownTimeRemaining) {
                return toast.error(`🔒 Aguarde ${cooldownTimeRemaining} para o próximo envio!`);
            }
        }

        if (!wantsWhatsapp && !wantsTelegram && !wantsInstagram && !wantsStatus) {
            return toast.error("Selecione pelo menos um canal de envio!");
        }

        if (!divulgadorTexto && !divulgadorMedia && !savedPostMediaPath) {
            return toast.error("Insira o texto ou anexe uma foto/vídeo!");
        }

        if (divulgadorScheduleType === 'once' && !divulgadorScheduledAt) {
            return toast.error("Selecione a data e o horário para o disparo agendado!");
        }

        if (divulgadorScheduleType === 'recurring') {
            if (divulgadorRecurringDays.length === 0) {
                return toast.error("Selecione pelo menos um dia da semana para a repetição!");
            }
            if (divulgadorRecurringMode === 'fixed_times') {
                if (!divulgadorRecurringTimes || !divulgadorRecurringTimes.trim()) {
                    return toast.error("Insira pelo menos um horário de disparo (ex: 09:00)!");
                }
            } else if (divulgadorRecurringMode === 'interval') {
                if (!divulgadorIntervalMinutes || parseInt(divulgadorIntervalMinutes, 10) <= 0) {
                    return toast.error("Selecione um intervalo de tempo válido!");
                }
            }
        }

        setSendingDivulgador(true);
        const actionLabel = divulgadorScheduleType === 'now' ? "Enviando postagem própria..." : "Agendando publicação...";
        const loadToast = toast.loading(actionLabel);

        try {
            const formData = new FormData();
            const autoTitle = savedPostTitle || (divulgadorTexto ? divulgadorTexto.substring(0, 30) : `Publicação ${new Date().toLocaleDateString('pt-BR')}`);
            formData.append('title', autoTitle);
            formData.append('texto', divulgadorTexto || '');
            if (divulgadorMedia) {
                formData.append('media', divulgadorMedia);
            }
            if (savedPostMediaPath) {
                formData.append('savedMediaPath', savedPostMediaPath);
            }
            formData.append('scheduleType', divulgadorScheduleType);
            if (divulgadorScheduleType === 'once') {
                formData.append('scheduledAt', divulgadorScheduledAt);
            }
            if (divulgadorScheduleType === 'recurring') {
                formData.append('recurringDays', divulgadorRecurringDays.sort((a, b) => a - b).join(','));
                formData.append('recurringMode', divulgadorRecurringMode);
                formData.append('recurringTimes', divulgadorRecurringTimes || '09:00');
                formData.append('intervalMinutes', divulgadorIntervalMinutes);
                formData.append('intervalStart', divulgadorIntervalStart || '08:00');
                formData.append('intervalEnd', divulgadorIntervalEnd || '22:00');
            }
            formData.append('postarWhatsapp', wantsWhatsapp);
            formData.append('postarTelegram', wantsTelegram);
            formData.append('postarInstagram', wantsInstagram);
            formData.append('postarInstagramType', divulgadorPostarIgType);
            formData.append('postarStatus', wantsStatus);
            formData.append('status', 'active');

            await api.post('/saved-posts', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (divulgadorScheduleType === 'now') {
                toast.success("Publicação enviada com sucesso!", { id: loadToast });
            } else if (divulgadorScheduleType === 'once') {
                toast.success("Publicação agendada com sucesso!", { id: loadToast });
            } else {
                toast.success("Agendamento recorrente configurado com sucesso!", { id: loadToast });
            }

            setDivulgadorTexto('');
            setDivulgadorMedia(null);
            setSavedPostMediaPath(null);
            setSavedPostTitle('');
            fetchSavedPosts();

            if (userPlan === 'free' || userPlan === 'free_divulgador') {
                const resConfig = await api.get('/config');
                setUserConfig(resConfig.data);
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || "Erro ao processar postagem.", { id: loadToast });
        } finally {
            setSendingDivulgador(false);
        }
    };

    const handleSavePost = async () => {
        if (!savedPostTitle) {
            return toast.error("Dê um título para sua publicação!");
        }
        if (!divulgadorTexto && !divulgadorMedia && !savedPostMediaPath) {
            return toast.error("Insira texto ou anexe uma mídia para salvar!");
        }

        setIsSavingPost(true);
        const loadToast = toast.loading("Salvando publicação...");

        try {
            const formData = new FormData();
            formData.append('title', savedPostTitle);
            if (divulgadorTexto) formData.append('texto', divulgadorTexto);
            if (divulgadorMedia) formData.append('media', divulgadorMedia);
            if (savedPostMediaPath) formData.append('savedMediaPath', savedPostMediaPath);
            formData.append('scheduleType', divulgadorScheduleType);
            if (divulgadorScheduleType === 'once' && divulgadorScheduledAt) {
                formData.append('scheduledAt', divulgadorScheduledAt);
            }
            if (divulgadorScheduleType === 'recurring') {
                formData.append('recurringDays', divulgadorRecurringDays.sort((a, b) => a - b).join(','));
                formData.append('recurringMode', divulgadorRecurringMode);
                formData.append('recurringTimes', divulgadorRecurringTimes || '09:00');
                formData.append('intervalMinutes', divulgadorIntervalMinutes);
                formData.append('intervalStart', divulgadorIntervalStart || '08:00');
                formData.append('intervalEnd', divulgadorIntervalEnd || '22:00');
            }
            formData.append('postarWhatsapp', divulgadorPostarWa);
            formData.append('postarTelegram', divulgadorPostarTg);
            formData.append('postarInstagram', divulgadorPostarIg);
            formData.append('postarInstagramType', divulgadorPostarIgType);
            formData.append('postarStatus', divulgadorPostarStatus);
            formData.append('status', divulgadorScheduleType === 'now' ? 'completed' : 'active');

            await api.post('/saved-posts', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success("Publicação salva com sucesso!", { id: loadToast });
            setSavedPostTitle("");
            fetchSavedPosts();
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.error || "Erro ao salvar publicação.", { id: loadToast });
        } finally {
            setIsSavingPost(false);
        }
    };

    const handleQuickSend = async (postId, postTitle) => {
        setQuickSendingId(postId);
        const loadToast = toast.loading(`Disparando "${postTitle || 'Publicação'}"...`);
        try {
            const res = await api.post(`/saved-posts/${postId}/disparar`);
            toast.success(res.data.message || "Publicação disparada com sucesso!", { id: loadToast });
            fetchSavedPosts();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || "Erro ao disparar publicação.", { id: loadToast });
        } finally {
            setQuickSendingId(null);
        }
    };

    const handleTogglePostStatus = async (post) => {
        const newStatus = post.status === 'active' ? 'paused' : 'active';
        const loadToast = toast.loading(newStatus === 'active' ? "Ativando agendamento..." : "Pausando agendamento...");
        try {
            await api.put(`/saved-posts/${post.id}`, { status: newStatus });
            toast.success(newStatus === 'active' ? "Agendamento ativado!" : "Agendamento pausado!", { id: loadToast });
            fetchSavedPosts();
        } catch (err) {
            console.error(err);
            toast.error("Erro ao alterar status do agendamento.", { id: loadToast });
        }
    };

    const handleOpenScheduleModal = (post) => {
        setSelectedPostForSchedule(post);
        setModalScheduleType(post.scheduleType || 'now');
        
        if (post.scheduledAt) {
            const d = new Date(post.scheduledAt);
            const pad = (n) => n.toString().padStart(2, '0');
            const formatted = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
            setModalScheduledAt(formatted);
        } else {
            setModalScheduledAt('');
        }

        const days = post.recurringDays ? post.recurringDays.split(',').map(d => parseInt(d.trim(), 10)).filter(n => !isNaN(n)) : [0, 1, 2, 3, 4, 5, 6];
        setModalRecurringDays(days);
        setModalRecurringMode(post.recurringMode || 'fixed_times');
        setModalRecurringTimes(post.recurringTimes || '09:00');
        setModalIntervalMinutes(post.intervalMinutes || 60);
        setModalIntervalStart(post.intervalStart || '08:00');
        setModalIntervalEnd(post.intervalEnd || '22:00');
        setModalPostarWa(post.postarWhatsapp !== false);
        setModalPostarStatus(Boolean(post.postarStatus));
        setModalPostarTg(post.postarTelegram !== false);
        setModalPostarIg(Boolean(post.postarInstagram));
        setModalPostarIgType(post.postarInstagramType || 'both');
        setModalStatus(post.status || 'active');
        setScheduleModalOpen(true);
    };

    const handleSaveScheduleModal = async () => {
        if (!selectedPostForSchedule) return;

        if (modalScheduleType === 'once' && !modalScheduledAt) {
            return toast.error("Selecione a data e a hora do agendamento!");
        }

        if (modalScheduleType === 'recurring') {
            if (modalRecurringDays.length === 0) {
                return toast.error("Selecione pelo menos um dia da semana!");
            }
            if (modalRecurringMode === 'fixed_times' && (!modalRecurringTimes || !modalRecurringTimes.trim())) {
                return toast.error("Informe os horários de disparo!");
            }
            if (modalRecurringMode === 'interval' && (!modalIntervalMinutes || parseInt(modalIntervalMinutes, 10) <= 0)) {
                return toast.error("Informe o intervalo de tempo!");
            }
        }

        setIsSavingScheduleModal(true);
        const loadToast = toast.loading("Salvando configurações de agendamento...");

        try {
            await api.put(`/saved-posts/${selectedPostForSchedule.id}`, {
                scheduleType: modalScheduleType,
                scheduledAt: modalScheduleType === 'once' ? modalScheduledAt : null,
                recurringDays: modalRecurringDays.sort((a, b) => a - b).join(','),
                recurringMode: modalRecurringMode,
                recurringTimes: modalRecurringTimes,
                intervalMinutes: modalIntervalMinutes,
                intervalStart: modalIntervalStart,
                intervalEnd: modalIntervalEnd,
                postarWhatsapp: modalPostarWa,
                postarStatus: modalPostarStatus,
                postarTelegram: modalPostarTg,
                postarInstagram: modalPostarIg,
                postarInstagramType: modalPostarIgType,
                status: modalStatus
            });

            toast.success("Configurações atualizadas com sucesso!", { id: loadToast });
            setScheduleModalOpen(false);
            setSelectedPostForSchedule(null);
            fetchSavedPosts();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || "Erro ao atualizar agendamento.", { id: loadToast });
        } finally {
            setIsSavingScheduleModal(false);
        }
    };

    const handleDeleteSavedPost = async (id) => {
        const confirmed = await confirm({
            title: "Excluir Publicação",
            message: "Deseja realmente excluir esta publicação salva? Esta ação não pode ser desfeita.",
            confirmText: "Sim, excluir",
            cancelText: "Cancelar",
            type: "danger"
        });
        if (!confirmed) return;
        
        try {
            await api.delete(`/saved-posts/${id}`);
            toast.success("Publicação excluída.");
            fetchSavedPosts();
        } catch (error) {
            toast.error("Erro ao excluir publicação.");
        }
    };

    const handleSelectSavedPost = (post) => {
        setDivulgadorTexto(post.texto || "");
        setDivulgadorMedia(null);
        if (post.mediaPath) {
            setSavedPostMediaPath(post.mediaPath);
        } else {
            setSavedPostMediaPath(null);
        }
        if (post.scheduleType) setDivulgadorScheduleType(post.scheduleType);
        if (post.recurringMode) setDivulgadorRecurringMode(post.recurringMode);
        if (post.recurringTimes) setDivulgadorRecurringTimes(post.recurringTimes);
        if (post.intervalMinutes) setDivulgadorIntervalMinutes(post.intervalMinutes);
        if (post.intervalStart) setDivulgadorIntervalStart(post.intervalStart);
        if (post.intervalEnd) setDivulgadorIntervalEnd(post.intervalEnd);
        if (post.postarWhatsapp !== undefined) setDivulgadorPostarWa(post.postarWhatsapp);
        if (post.postarStatus !== undefined) setDivulgadorPostarStatus(post.postarStatus);
        if (post.postarTelegram !== undefined) setDivulgadorPostarTg(post.postarTelegram);
        if (post.postarInstagram !== undefined) setDivulgadorPostarIg(post.postarInstagram);
        if (post.postarInstagramType) setDivulgadorPostarIgType(post.postarInstagramType);

        window.scrollTo({ top: 0, behavior: 'smooth' });
        toast.success("Postagem carregada para envio no formulário!");
    };

    const MultiSelectBtn = ({ active, onClick, label, color, icon, textColor = "#fff", disabled = false }) => (
        <div
            onClick={!disabled ? onClick : null}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 18px',
                borderRadius: '10px',
                border: active ? `2px solid ${color}` : '2px solid #e2e8f0',
                backgroundColor: active ? color : '#fff',
                color: active ? textColor : '#94a3b8',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                fontWeight: '600',
                transition: '0.2s all',
                position: 'relative'
            }}
        >
            {icon}
            <span>{label}</span>
            {active && <div style={{ position: 'absolute', top: '-5px', right: '-5px', background: '#22c55e', color: 'white', borderRadius: '50%', width: '18px', height: '18px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white' }}>✓</div>}
        </div>
    );

    const activePlatform = userConfig?.activePlatform;
    const isShopee = activePlatform === 'shopee';
    const isMagalu = activePlatform === 'magalu';

    const converterTitle = userPlan === 'free'
        ? (isMagalu ? "🔗 Conversor de Link (Magalu)" : "🔗 Conversor de Link (Shopee)")
        : "🔗 Conversor de Link";

    const converterDesc = userPlan === 'free'
        ? (isMagalu
            ? "Cole links de ofertas da Magazine Luiza para encurtá-los e convertê-los automaticamente para o seu ID de afiliado."
            : "Cole links de ofertas da Shopee para encurtá-los e convertê-los automaticamente para o seu ID de afiliado.")
        : "Cole links de ofertas da Shopee, Magazine Luiza, Mercado Livre, Amazon, AliExpress ou Shein para encurtá-los e convertê-los automaticamente para o seu ID de afiliado.";

    const converterPlaceholder = userPlan === 'free'
        ? (isMagalu
            ? "Cole o link da Magalu aqui... Ex: magazineluiza.com.br/..."
            : "Cole o link da Shopee aqui... Ex: shopee.com.br/...")
        : "Cole o link da oferta aqui... Ex: shopee.com.br, amazon.com.br, etc.";

    const renderConverterAndTemplate = () => {
        return (
            <>
                {/* CONVERSOR DE LINKS */}
                <div style={{
                    background: 'white',
                    padding: '30px',
                    borderRadius: '20px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                            <div style={{ backgroundColor: '#fff5f1', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Link size={24} color="#ee4d2d" />
                            </div>
                            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px', fontWeight: '800' }}>{converterTitle}</h3>
                        </div>
                        <p style={{ color: '#64748b', fontSize: '13px', lineHeight: '1.6', marginBottom: '20px' }}>
                            {converterDesc}
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <input
                                type="text"
                                placeholder={converterPlaceholder}
                                value={freeLinkInput}
                                onChange={(e) => setFreeLinkInput(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '14px 16px',
                                    borderRadius: '10px',
                                    border: '2px solid #e2e8f0',
                                    fontSize: '14px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                            <button
                                onClick={handleFreeConvert}
                                disabled={convertingFree}
                                style={{
                                    backgroundColor: convertingFree ? '#94a3b8' : '#ee4d2d',
                                    color: 'white',
                                    border: 'none',
                                    padding: '14px',
                                    borderRadius: '10px',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    cursor: convertingFree ? 'not-allowed' : 'pointer',
                                    transition: '0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                {convertingFree ? <Loader2 size={16} className="animate-spin" /> : <Play size={14} fill="white" />}
                                {convertingFree ? 'CONVERTENDO...' : 'CONVERTER LINK'}
                            </button>
                        </div>

                        {convertedProduct && (
                            <div style={{
                                marginTop: '20px',
                                padding: '15px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '12px',
                                border: '1px solid #e2e8f0',
                                animation: 'fadeIn 0.3s ease-out'
                            }}>
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    {convertedProduct.image && (
                                        <img
                                            src={convertedProduct.image}
                                            alt="Produto"
                                            style={{ width: '70px', height: '70px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                                        />
                                    )}
                                    <div style={{ flex: 1, textAlign: 'left' }}>
                                        <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e293b', marginBottom: '6px', lineHeight: '1.4' }}>
                                            {convertedProduct.productName}
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px', fontSize: '12px', alignItems: 'center' }}>
                                            {convertedProduct.oldPrice && (
                                                <span style={{ color: '#94a3b8', textDecoration: 'line-through' }}>
                                                    De: R$ {convertedProduct.oldPrice}
                                                </span>
                                            )}
                                            <span style={{ color: '#22c55e', fontWeight: '800' }}>
                                                Por: R$ {convertedProduct.price}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ marginTop: '15px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '6px', textAlign: 'left' }}>Link Convertido:</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input
                                            type="text"
                                            readOnly
                                            value={convertedLink}
                                            style={{
                                                flex: 1,
                                                padding: '8px 12px',
                                                borderRadius: '6px',
                                                border: '1px solid #cbd5e1',
                                                backgroundColor: '#fff',
                                                fontSize: '12px',
                                                color: '#1e293b',
                                                outline: 'none'
                                            }}
                                        />
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(convertedLink);
                                                toast.success("Link copiado!");
                                            }}
                                            style={{
                                                padding: '8px 14px',
                                                backgroundColor: '#0f172a',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Copiar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Banner de Upgrade */}
                    {userPlan === 'free' && (
                        <div style={{
                            backgroundColor: '#fffbeb',
                            border: '1px solid #fef3c7',
                            padding: '15px',
                            borderRadius: '12px',
                            marginTop: '25px',
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'flex-start'
                        }}>
                            <span style={{ fontSize: '18px' }}>💡</span>
                            <p style={{ margin: 0, fontSize: '12px', color: '#b45309', lineHeight: '1.5', textAlign: 'left' }}>
                                Quer converter links do <strong>{isShopee ? 'Magazine Luiza' : 'Shopee'}, Mercado Livre, Amazon, AliExpress e Shein</strong>? Faça upgrade para o Plano Básico!
                            </p>
                        </div>
                    )}
                </div>

                {/* MODELO DE MENSAGEM */}
                <div style={{
                    background: 'white',
                    padding: '30px',
                    borderRadius: '20px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                            <div style={{ backgroundColor: '#f5f3ff', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <MessageSquare size={24} color="#8b5cf6" />
                            </div>
                            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px', fontWeight: '800' }}>📝 Modelo de Mensagem</h3>
                        </div>
                        <p style={{ color: '#64748b', fontSize: '13px', lineHeight: '1.6', marginBottom: '20px' }}>
                            {userPlan === 'free' 
                                ? <>Configure o formato da sua oferta. Use <code>{"{nome}"}</code>, <code>{"{precoAntigo}"}</code>, <code>{"{preco}"}</code>, <code>{"{link}"}</code> e <code>{"{loja}"}</code>.</>
                                : <>Visualize e copie a oferta formatada. Para editar permanentemente o template das mensagens automáticas do robô, acesse a aba de <strong>Configurações</strong>.</>
                            }
                        </p>

                        <textarea
                            value={messageTemplate}
                            onChange={(e) => {
                                setMessageTemplate(e.target.value);
                                if (userPlan === 'free') {
                                    localStorage.setItem('free_msg_template', e.target.value);
                                } else {
                                    localStorage.setItem('non_free_msg_template', e.target.value);
                                }
                            }}
                            style={{
                                width: '100%',
                                height: '110px',
                                padding: '12px 16px',
                                borderRadius: '10px',
                                border: '2px solid #e2e8f0',
                                fontSize: '13px',
                                fontFamily: 'monospace',
                                outline: 'none',
                                resize: 'none',
                                marginBottom: '10px',
                                boxSizing: 'border-box'
                            }}
                        />

                        {/* PREVIEW DA MENSAGEM FORMATADA */}
                        {convertedLink && (
                            <div style={{ marginTop: '15px', animation: 'fadeIn 0.3s ease-out' }}>
                                <label style={{ fontSize: '12px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '6px' }}>Preview da Mensagem:</label>
                                <div style={{
                                    padding: '12px 16px',
                                    backgroundColor: '#f8fafc',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    whiteSpace: 'pre-wrap',
                                    color: '#334155',
                                    maxHeight: '130px',
                                    overflowY: 'auto',
                                    marginBottom: '12px',
                                    textAlign: 'left'
                                }}>
                                    {formatPreviewMessage()}
                                </div>
                                <button
                                    onClick={handleCopyFormattedMessage}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        backgroundColor: '#8b5cf6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        transition: '0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#7c3aed'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#8b5cf6'}
                                >
                                    Copiar Mensagem Formatada
                                </button>
                            </div>
                        )}
                    </div>

                    {!convertedLink && (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>
                            Converta um link para visualizar a mensagem formatada.
                        </div>
                    )}
                </div>
            </>
        );
    };

    const renderDivulgadorPanel = () => {
        const canInstagram = (userPlan === 'premium' || userPlan === 'premium_divulgador' || userRole === 'admin') && !!userConfig?.instagramBusinessAccountId;

        return (
            <div style={{ animation: 'fadeIn 0.3s ease-out', width: '100%' }}>
                {/* CARD CRIAÇÃO / AGENDAMENTO DE POSTAGEM */}
                <div style={{
                    background: 'white',
                    padding: '30px',
                    borderRadius: '20px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                    border: '1px solid #e2e8f0',
                    maxWidth: '850px',
                    margin: '0 auto 30px auto'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                        <div style={{ backgroundColor: '#eff6ff', padding: '12px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Store size={26} color="#3b82f6" />
                        </div>
                        <div style={{ textAlign: 'left' }}>
                            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '20px', fontWeight: '800' }}>
                                {isAdmin ? 'Divulgação de Marketing & Promoções' : 'Postagem Própria & Agendamentos'}
                            </h3>
                            <p style={{ margin: 0, color: '#64748b', fontSize: '13px', marginTop: '4px' }}>
                                {isAdmin 
                                    ? 'Crie comunicados, avisos ou conteúdos promocionais da ferramenta para enviar agora ou agendar disparos únicos e recorrentes.'
                                    : 'Crie anúncios, avisos ou conteúdos personalizados para enviar agora ou agendar disparos únicos e recorrentes.'}
                            </p>
                        </div>
                    </div>

                    {/* Banner de Aviso se o WhatsApp de Marketing estiver desativado nas Configurações */}
                    {isAdmin && userConfig?.whatsappMarketingActive === false && (
                        <div style={{
                            backgroundColor: '#fff1f2',
                            border: '1px solid #fecdd3',
                            borderLeft: '4px solid #f43f5e',
                            borderRadius: '12px',
                            padding: '14px 18px',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '12px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#9f1239', fontSize: '13px', textAlign: 'left' }}>
                                <span style={{ fontSize: '18px' }}>🔇</span>
                                <span>
                                    <strong>WhatsApp de Marketing Desativado:</strong> A Instância A (Marketing) está desligada nas Configurações do Sistema. Ative-a no menu Configurações para realizar disparos por WhatsApp.
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => onNavigateTab && onNavigateTab('config')}
                                style={{
                                    backgroundColor: '#e11d48',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '6px 14px',
                                    borderRadius: '8px',
                                    fontWeight: '700',
                                    fontSize: '12px',
                                    cursor: 'pointer'
                                }}
                            >
                                Ir para Configurações &rarr;
                            </button>
                        </div>
                    )}

                    {/* Banner de Atenção se não houver grupos ativos (Apenas usuários comuns que não sejam Free) */}
                    {!isAdmin && userPlan !== 'free' && userPlan !== 'free_divulgador' && activeGroupsCount === 0 && (
                        <div style={{
                            backgroundColor: '#fffbeb',
                            border: '1px solid #fef3c7',
                            borderLeft: '4px solid #f59e0b',
                            borderRadius: '12px',
                            padding: '14px 18px',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '12px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#92400e', fontSize: '13px', textAlign: 'left' }}>
                                <Users size={20} color="#d97706" style={{ flexShrink: 0 }} />
                                <span>
                                    <strong>Atenção:</strong> Você ainda não possui nenhum grupo ou canal ativo. Antes de disparar, adicione e ative seus grupos de destino!
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => onNavigateTab && onNavigateTab('grupos')}
                                style={{
                                    backgroundColor: '#f59e0b',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '6px 14px',
                                    borderRadius: '8px',
                                    fontWeight: '700',
                                    fontSize: '12px',
                                    cursor: 'pointer'
                                }}
                            >
                                Configurar Grupos &rarr;
                            </button>
                        </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                        {/* Texto */}
                        <div style={{ textAlign: 'left' }}>
                            <label style={{ fontSize: '14px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '8px' }}>
                                Texto da Mensagem (Opcional)
                            </label>
                            <textarea
                                value={divulgadorTexto}
                                onChange={(e) => setDivulgadorTexto(e.target.value)}
                                placeholder="Digite a legenda da foto, a notícia, aviso ou promoção..."
                                style={{
                                    width: '100%',
                                    height: '130px',
                                    padding: '16px',
                                    borderRadius: '12px',
                                    border: '2px solid #e2e8f0',
                                    fontSize: '14px',
                                    outline: 'none',
                                    resize: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        {/* Mídia */}
                        <div style={{ textAlign: 'left' }}>
                            <label style={{ fontSize: '14px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '8px' }}>
                                Anexar Mídia (Foto ou Vídeo)
                            </label>
                            {savedPostMediaPath && (
                                <div style={{ marginBottom: '10px', padding: '10px 14px', backgroundColor: '#ecfdf5', border: '1px solid #10b981', borderRadius: '10px', color: '#047857', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <img 
                                            src={resolveMediaUrl(savedPostMediaPath)} 
                                            alt="Preview" 
                                            style={{ width: '38px', height: '38px', borderRadius: '8px', objectFit: 'cover' }} 
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                        <div>
                                            <strong>Mídia carregada do post salvo</strong>
                                            <div style={{ fontSize: '11px', color: '#64748b' }}>{savedPostMediaPath}</div>
                                        </div>
                                    </div>
                                    <button onClick={() => setSavedPostMediaPath(null)} style={{ background: 'none', border: 'none', color: '#047857', cursor: 'pointer', fontWeight: 'bold' }}>Remover</button>
                                </div>
                            )}
                            {(!userPlan || userPlan === 'free' || userPlan === 'free_divulgador') ? (
                                <div style={{ 
                                    padding: '16px', 
                                    border: '2px dashed #cbd5e1', 
                                    borderRadius: '12px', 
                                    backgroundColor: '#f8fafc',
                                    color: '#64748b',
                                    fontSize: '13px',
                                    textAlign: 'center'
                                }}>
                                    🔒 <strong>Upload de Fotos/Vídeos Bloqueado no Plano Free:</strong> O envio de fotos e vídeos é liberado a partir do <strong>Plano Básico</strong>.
                                </div>
                            ) : (
                                <input
                                    type="file"
                                    accept="image/*,video/*"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files.length > 0) {
                                            setDivulgadorMedia(e.target.files[0]);
                                            setSavedPostMediaPath(null);
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        border: '2px dashed #94a3b8',
                                        backgroundColor: '#f8fafc',
                                        cursor: 'pointer'
                                    }}
                                />
                            )}
                        </div>

                        {/* CANAIS DE ENVIO (DESTINOS) */}
                        <div style={{ textAlign: 'left', background: '#f8fafc', padding: '18px', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                            <label style={{ fontSize: '13px', fontWeight: '800', color: '#1e293b', display: 'block', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                📡 Canais de Envio (Destinos)
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                                {/* WhatsApp Grupos */}
                                <button
                                    type="button"
                                    onClick={() => setDivulgadorPostarWa(!divulgadorPostarWa)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        border: divulgadorPostarWa ? '2px solid #25D366' : '2px solid #cbd5e1',
                                        backgroundColor: divulgadorPostarWa ? '#f0fdf4' : '#fff',
                                        color: divulgadorPostarWa ? '#166534' : '#64748b',
                                        cursor: 'pointer',
                                        fontWeight: '700',
                                        fontSize: '13px',
                                        transition: '0.2s'
                                    }}
                                >
                                    <WhatsappIcon size={18} color={divulgadorPostarWa ? "#25D366" : "#94a3b8"} />
                                    <span>WhatsApp Grupos</span>
                                    {divulgadorPostarWa && <Check size={14} style={{ marginLeft: 'auto' }} />}
                                </button>

                                {/* WhatsApp Status */}
                                <button
                                    type="button"
                                    onClick={() => setDivulgadorPostarStatus(!divulgadorPostarStatus)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        border: divulgadorPostarStatus ? '2px solid #10b981' : '2px solid #cbd5e1',
                                        backgroundColor: divulgadorPostarStatus ? '#ecfdf5' : '#fff',
                                        color: divulgadorPostarStatus ? '#047857' : '#64748b',
                                        cursor: 'pointer',
                                        fontWeight: '700',
                                        fontSize: '13px',
                                        transition: '0.2s'
                                    }}
                                >
                                    <WhatsappIcon size={18} color={divulgadorPostarStatus ? "#10b981" : "#94a3b8"} />
                                    <span>WhatsApp Status</span>
                                    {divulgadorPostarStatus && <Check size={14} style={{ marginLeft: 'auto' }} />}
                                </button>

                                {/* Telegram */}
                                <button
                                    type="button"
                                    onClick={() => setDivulgadorPostarTg(!divulgadorPostarTg)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        border: divulgadorPostarTg ? '2px solid #0088cc' : '2px solid #cbd5e1',
                                        backgroundColor: divulgadorPostarTg ? '#f0f9ff' : '#fff',
                                        color: divulgadorPostarTg ? '#0369a1' : '#64748b',
                                        cursor: 'pointer',
                                        fontWeight: '700',
                                        fontSize: '13px',
                                        transition: '0.2s'
                                    }}
                                >
                                    <TelegramIcon size={18} color={divulgadorPostarTg ? "#0088cc" : "#94a3b8"} />
                                    <span>Telegram</span>
                                    {divulgadorPostarTg && <Check size={14} style={{ marginLeft: 'auto' }} />}
                                </button>

                                {/* Instagram */}
                                {canInstagram && (
                                    <button
                                        type="button"
                                        onClick={() => setDivulgadorPostarIg(!divulgadorPostarIg)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            padding: '10px 14px',
                                            borderRadius: '10px',
                                            border: divulgadorPostarIg ? '2px solid #e1306c' : '2px solid #cbd5e1',
                                            backgroundColor: divulgadorPostarIg ? '#fdf2f8' : '#fff',
                                            color: divulgadorPostarIg ? '#be185d' : '#64748b',
                                            cursor: 'pointer',
                                            fontWeight: '700',
                                            fontSize: '13px',
                                            transition: '0.2s'
                                        }}
                                    >
                                        <InstagramIcon size={18} color={divulgadorPostarIg ? "#e1306c" : "#94a3b8"} />
                                        <span>Instagram</span>
                                        {divulgadorPostarIg && <Check size={14} style={{ marginLeft: 'auto' }} />}
                                    </button>
                                )}
                            </div>

                            {/* Dropdown onde postar no Instagram se ativo */}
                            {divulgadorPostarIg && canInstagram && (
                                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>Formato Instagram:</span>
                                    <select
                                        value={divulgadorPostarIgType}
                                        onChange={(e) => setDivulgadorPostarIgType(e.target.value)}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: '8px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '12px',
                                            color: '#334155',
                                            fontWeight: '600',
                                            outline: 'none'
                                        }}
                                    >
                                        <option value="both">Feed e Stories (Ambos)</option>
                                        <option value="feed">Apenas no Feed</option>
                                        <option value="story">Apenas nos Stories</option>
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* MODO DE DISPARO / AGENDAMENTO */}
                        <div style={{ textAlign: 'left', background: '#f1f5f9', padding: '20px', borderRadius: '16px', border: '1px solid #cbd5e1' }}>
                            <label style={{ fontSize: '13px', fontWeight: '800', color: '#1e293b', display: 'block', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                ⏰ Modo de Disparo & Horários
                            </label>

                            {/* 3 Opções de Modo */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                                <button
                                    type="button"
                                    onClick={() => setDivulgadorScheduleType('now')}
                                    style={{
                                        padding: '14px',
                                        borderRadius: '12px',
                                        border: divulgadorScheduleType === 'now' ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                                        backgroundColor: divulgadorScheduleType === 'now' ? '#eff6ff' : '#fff',
                                        color: divulgadorScheduleType === 'now' ? '#1d4ed8' : '#64748b',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: '0.2s'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Send size={16} /> <span>Publicar Agora</span>
                                    </div>
                                    <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#64748b' }}>Disparo imediato</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setDivulgadorScheduleType('once')}
                                    style={{
                                        padding: '14px',
                                        borderRadius: '12px',
                                        border: divulgadorScheduleType === 'once' ? '2px solid #8b5cf6' : '1px solid #cbd5e1',
                                        backgroundColor: divulgadorScheduleType === 'once' ? '#f5f3ff' : '#fff',
                                        color: divulgadorScheduleType === 'once' ? '#6d28d9' : '#64748b',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: '0.2s'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Calendar size={16} /> <span>Uma vez só</span>
                                    </div>
                                    <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#64748b' }}>Data e hora fixa (ex: Feed)</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setDivulgadorScheduleType('recurring')}
                                    style={{
                                        padding: '14px',
                                        borderRadius: '12px',
                                        border: divulgadorScheduleType === 'recurring' ? '2px solid #10b981' : '1px solid #cbd5e1',
                                        backgroundColor: divulgadorScheduleType === 'recurring' ? '#ecfdf5' : '#fff',
                                        color: divulgadorScheduleType === 'recurring' ? '#047857' : '#64748b',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: '0.2s'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Repeat size={16} /> <span>Repetida / Recorrente</span>
                                    </div>
                                    <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#64748b' }}>Diário ou dias da semana (ex: Stories)</span>
                                </button>
                            </div>

                            {/* Detalhes para "Uma vez só" */}
                            {divulgadorScheduleType === 'once' && (
                                <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155' }}>
                                        📅 Escolha a Data e o Horário do Disparo:
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={divulgadorScheduledAt}
                                        onChange={(e) => setDivulgadorScheduledAt(e.target.value)}
                                        style={{
                                            padding: '12px 14px',
                                            borderRadius: '8px',
                                            border: '2px solid #cbd5e1',
                                            fontSize: '14px',
                                            outline: 'none',
                                            fontWeight: '600',
                                            maxWidth: '300px'
                                        }}
                                    />
                                    <p style={{ margin: 0, fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>
                                        O robô disparará esta publicação uma única vez exatamente na data e hora selecionadas.
                                    </p>
                                </div>
                            )}

                            {/* Detalhes para "Repetida / Recorrente" */}
                            {divulgadorScheduleType === 'recurring' && (
                                <div style={{ background: '#fff', padding: '18px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    {/* Dias da semana */}
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155' }}>
                                                📆 Dias de Execução:
                                            </label>
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setDivulgadorRecurringDays([0, 1, 2, 3, 4, 5, 6])}
                                                    style={{ fontSize: '11px', background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', color: '#475569' }}
                                                >
                                                    Todos os dias
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setDivulgadorRecurringDays([1, 2, 3, 4, 5])}
                                                    style={{ fontSize: '11px', background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', color: '#475569' }}
                                                >
                                                    Seg a Sex
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setDivulgadorRecurringDays([0, 6])}
                                                    style={{ fontSize: '11px', background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', color: '#475569' }}
                                                >
                                                    Fim de semana
                                                </button>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            {DIAS_SEMANA_LIST.map((dia) => {
                                                const selected = divulgadorRecurringDays.includes(dia.id);
                                                return (
                                                    <button
                                                        key={dia.id}
                                                        type="button"
                                                        onClick={() => {
                                                            if (selected) {
                                                                setDivulgadorRecurringDays(divulgadorRecurringDays.filter(d => d !== dia.id));
                                                            } else {
                                                                setDivulgadorRecurringDays([...divulgadorRecurringDays, dia.id]);
                                                            }
                                                        }}
                                                        style={{
                                                            padding: '8px 14px',
                                                            borderRadius: '8px',
                                                            border: selected ? '2px solid #10b981' : '1px solid #cbd5e1',
                                                            backgroundColor: selected ? '#10b981' : '#fff',
                                                            color: selected ? '#fff' : '#64748b',
                                                            fontWeight: 'bold',
                                                            fontSize: '13px',
                                                            cursor: 'pointer',
                                                            transition: '0.2s'
                                                        }}
                                                    >
                                                        {dia.nome}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Alternador de Modo de Programação Recorrente */}
                                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '14px' }}>
                                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '8px' }}>
                                            ⏱️ Modo de Programação dos Disparos:
                                        </label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                                            <button
                                                type="button"
                                                onClick={() => setDivulgadorRecurringMode('fixed_times')}
                                                style={{
                                                    padding: '10px',
                                                    borderRadius: '8px',
                                                    border: divulgadorRecurringMode === 'fixed_times' ? '2px solid #10b981' : '1px solid #cbd5e1',
                                                    backgroundColor: divulgadorRecurringMode === 'fixed_times' ? '#ecfdf5' : '#fff',
                                                    color: divulgadorRecurringMode === 'fixed_times' ? '#047857' : '#64748b',
                                                    fontWeight: '700',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <Clock size={14} /> <span>Horários Específicos</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setDivulgadorRecurringMode('interval')}
                                                style={{
                                                    padding: '10px',
                                                    borderRadius: '8px',
                                                    border: divulgadorRecurringMode === 'interval' ? '2px solid #10b981' : '1px solid #cbd5e1',
                                                    backgroundColor: divulgadorRecurringMode === 'interval' ? '#ecfdf5' : '#fff',
                                                    color: divulgadorRecurringMode === 'interval' ? '#047857' : '#64748b',
                                                    fontWeight: '700',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <RotateCw size={14} /> <span>Intervalo de Tempo</span>
                                            </button>
                                        </div>

                                        {/* Opção 1: Horários Fixos */}
                                        {divulgadorRecurringMode === 'fixed_times' && (
                                            <div>
                                                <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '6px' }}>
                                                    ⏰ Horários de Disparo (separados por vírgula):
                                                </label>
                                                <input
                                                    type="text"
                                                    value={divulgadorRecurringTimes}
                                                    onChange={(e) => setDivulgadorRecurringTimes(e.target.value)}
                                                    placeholder="Ex: 09:00, 14:00, 19:30"
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 14px',
                                                        borderRadius: '8px',
                                                        border: '2px solid #cbd5e1',
                                                        fontSize: '14px',
                                                        outline: 'none',
                                                        fontWeight: '600',
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                                <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: '11px', color: '#64748b' }}>Atalhos rápidos:</span>
                                                    {['08:00', '10:00, 18:00', '09:00, 13:00, 18:00, 21:00'].map((sug, i) => (
                                                        <button
                                                            key={i}
                                                            type="button"
                                                            onClick={() => setDivulgadorRecurringTimes(sug)}
                                                            style={{ fontSize: '11px', background: '#f8fafc', border: '1px solid #cbd5e1', padding: '3px 8px', borderRadius: '6px', cursor: 'pointer', color: '#334155' }}
                                                        >
                                                            {sug}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Opção 2: Intervalo de Tempo / Frequência */}
                                        {divulgadorRecurringMode === 'interval' && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                <div>
                                                    <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '6px' }}>
                                                        ⏳ Intervalo entre os Disparos:
                                                    </label>
                                                    <select
                                                        value={divulgadorIntervalMinutes}
                                                        onChange={(e) => setDivulgadorIntervalMinutes(parseInt(e.target.value, 10))}
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px 14px',
                                                            borderRadius: '8px',
                                                            border: '2px solid #cbd5e1',
                                                            fontSize: '14px',
                                                            outline: 'none',
                                                            fontWeight: '600',
                                                            backgroundColor: '#fff',
                                                            cursor: 'pointer',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    >
                                                        {INTERVALO_OPCOES.map(opt => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '6px' }}>
                                                        🕒 Janela de Horário no Dia:
                                                    </label>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                                        <div>
                                                            <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Início (A partir das):</span>
                                                            <input
                                                                type="time"
                                                                value={divulgadorIntervalStart}
                                                                onChange={(e) => setDivulgadorIntervalStart(e.target.value)}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px',
                                                                    borderRadius: '8px',
                                                                    border: '2px solid #cbd5e1',
                                                                    fontSize: '14px',
                                                                    fontWeight: '600',
                                                                    outline: 'none',
                                                                    boxSizing: 'border-box'
                                                                }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Fim (Até as):</span>
                                                            <input
                                                                type="time"
                                                                value={divulgadorIntervalEnd}
                                                                onChange={(e) => setDivulgadorIntervalEnd(e.target.value)}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px',
                                                                    borderRadius: '8px',
                                                                    border: '2px solid #cbd5e1',
                                                                    fontSize: '14px',
                                                                    fontWeight: '600',
                                                                    outline: 'none',
                                                                    boxSizing: 'border-box'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div style={{ background: '#f0fdf4', padding: '10px 14px', borderRadius: '8px', border: '1px solid #bbf7d0', fontSize: '12px', color: '#166534' }}>
                                                    💡 O robô disparará automaticamente <strong>a cada {formatIntervalText(divulgadorIntervalMinutes)}</strong> entre <strong>{divulgadorIntervalStart}</strong> e <strong>{divulgadorIntervalEnd}</strong> nos dias selecionados.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Botão de Envio / Agendamento Principal */}
                        <div style={{ marginTop: '5px' }}>
                            <button
                                onClick={handleSendDivulgador}
                                disabled={sendingDivulgador}
                                style={{
                                    width: '100%',
                                    backgroundColor: sendingDivulgador 
                                        ? '#94a3b8' 
                                        : (divulgadorScheduleType === 'now' ? '#3b82f6' : (divulgadorScheduleType === 'once' ? '#8b5cf6' : '#10b981')),
                                    color: 'white',
                                    border: 'none',
                                    padding: '16px',
                                    borderRadius: '12px',
                                    fontWeight: '800',
                                    fontSize: '16px',
                                    cursor: sendingDivulgador ? 'not-allowed' : 'pointer',
                                    transition: '0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                }}
                            >
                                {sendingDivulgador ? (
                                    <Loader2 size={20} className="animate-spin" />
                                ) : divulgadorScheduleType === 'now' ? (
                                    <Send size={20} />
                                ) : divulgadorScheduleType === 'once' ? (
                                    <Calendar size={20} />
                                ) : (
                                    <Repeat size={20} />
                                )}
                                {sendingDivulgador 
                                    ? 'PROCESSANDO...' 
                                    : (divulgadorScheduleType === 'now' 
                                        ? 'ENVIAR AGORA' 
                                        : (divulgadorScheduleType === 'once' ? 'AGENDAR PUBLICAÇÃO ÚNICA' : 'ATIVAR AGENDAMENTO RECORRENTE'))
                                }
                            </button>
                        </div>

                        {/* Salvar como Publicação / Modelo */}
                        <div style={{ marginTop: '15px', paddingTop: '20px', borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <label style={{ fontSize: '13px', fontWeight: '700', color: '#475569' }}>
                                Salvar esta configuração como publicação/modelo para reutilizar
                            </label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <input
                                    type="text"
                                    placeholder="Dê um nome (ex: Promoção Fim de Semana / Stories Diários)"
                                    value={savedPostTitle}
                                    onChange={(e) => setSavedPostTitle(e.target.value)}
                                    style={{
                                        flex: 1,
                                        padding: '12px 16px',
                                        borderRadius: '10px',
                                        border: '2px solid #e2e8f0',
                                        outline: 'none',
                                        fontSize: '14px'
                                    }}
                                />
                                <button
                                    onClick={handleSavePost}
                                    disabled={isSavingPost}
                                    style={{
                                        backgroundColor: '#0f172a',
                                        color: 'white',
                                        border: 'none',
                                        padding: '0 22px',
                                        borderRadius: '10px',
                                        fontWeight: '700',
                                        cursor: isSavingPost ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    {isSavingPost ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SEÇÃO DE PUBLICAÇÕES SALVAS */}
                <div style={{
                    background: 'white',
                    padding: '30px',
                    borderRadius: '20px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                    border: '1px solid #e2e8f0',
                    maxWidth: '850px',
                    margin: '0 auto 30px auto'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            📂 Minhas Publicações & Agendamentos ({savedPosts.length})
                        </h3>
                        <button
                            onClick={fetchSavedPosts}
                            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600' }}
                        >
                            <RefreshCw size={14} className={isSavedPostsLoading ? "animate-spin" : ""} /> Atualizar
                        </button>
                    </div>

                    {isSavedPostsLoading ? (
                        <div style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>Carregando publicações salvas...</div>
                    ) : savedPosts.length === 0 ? (
                        <div style={{ padding: '30px', textAlign: 'center', color: '#64748b', backgroundColor: '#f8fafc', borderRadius: '12px', border: '2px dashed #cbd5e1' }}>
                            Você ainda não possui publicações salvas ou agendadas. Crie sua primeira no formulário acima!
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {savedPosts.map((post) => {
                                const isRecurring = post.scheduleType === 'recurring';
                                const isOnce = post.scheduleType === 'once';
                                const isActive = post.status === 'active';

                                return (
                                    <div
                                        key={post.id}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            padding: '20px',
                                            borderRadius: '16px',
                                            border: '1px solid #e2e8f0',
                                            backgroundColor: '#f8fafc',
                                            gap: '14px',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
                                            textAlign: 'left'
                                        }}
                                    >
                                        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                                            {/* Thumbnail de Mídia */}
                                            <div style={{ width: '90px', height: '90px', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#e2e8f0', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {post.mediaType === 'image' && (
                                                    <img 
                                                        src={resolveMediaUrl(post.mediaPath)} 
                                                        alt={post.title} 
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            console.warn('Erro ao carregar thumbnail do post salvo:', post.mediaPath);
                                                        }}
                                                    />
                                                )}
                                                {post.mediaType === 'video' && (
                                                    <video 
                                                        src={resolveMediaUrl(post.mediaPath)} 
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                                        muted 
                                                    />
                                                )}
                                                {post.mediaType === 'none' && <FileText size={28} color="#94a3b8" />}
                                            </div>

                                            {/* Informações do Post */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                                    <span style={{ fontWeight: '800', color: '#1e293b', fontSize: '16px' }}>{post.title}</span>

                                                    {/* Badge de Status de Agendamento */}
                                                    {isRecurring ? (
                                                        <span style={{
                                                            fontSize: '11px',
                                                            padding: '3px 8px',
                                                            borderRadius: '6px',
                                                            fontWeight: '700',
                                                            backgroundColor: isActive ? '#ecfdf5' : '#fef3c7',
                                                            color: isActive ? '#047857' : '#b45309',
                                                            border: isActive ? '1px solid #a7f3d0' : '1px solid #fde68a'
                                                        }}>
                                                            {isActive ? '🔁 Recorrente Ativo' : '⏸️ Recorrente Pausado'}
                                                        </span>
                                                    ) : isOnce ? (
                                                        <span style={{
                                                            fontSize: '11px',
                                                            padding: '3px 8px',
                                                            borderRadius: '6px',
                                                            fontWeight: '700',
                                                            backgroundColor: post.status === 'completed' ? '#f0fdf4' : '#eff6ff',
                                                            color: post.status === 'completed' ? '#166534' : '#1d4ed8',
                                                            border: post.status === 'completed' ? '1px solid #bbf7d0' : '1px solid #bfdbfe'
                                                        }}>
                                                            {post.status === 'completed' ? '✅ Disparo Concluído' : '📅 Agendado'}
                                                        </span>
                                                    ) : (
                                                        <span style={{
                                                            fontSize: '11px',
                                                            padding: '3px 8px',
                                                            borderRadius: '6px',
                                                            fontWeight: '700',
                                                            backgroundColor: '#f1f5f9',
                                                            color: '#475569',
                                                            border: '1px solid #cbd5e1'
                                                        }}>
                                                            ⚡ Manual / Imediato
                                                        </span>
                                                    )}
                                                </div>

                                                <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 10px 0', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                    {post.texto || "Sem texto."}
                                                </p>

                                                {/* Detalhes de Horários & Canais */}
                                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', fontSize: '12px', color: '#475569' }}>
                                                    {isOnce && post.scheduledAt && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#fff', padding: '4px 8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                            <Calendar size={13} color="#8b5cf6" />
                                                            <span>{formatDateTimeBR(post.scheduledAt)}</span>
                                                        </div>
                                                    )}

                                                    {isRecurring && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#fff', padding: '4px 8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                            <Clock size={13} color="#10b981" />
                                                            {post.recurringMode === 'interval' ? (
                                                                <span><strong>{formatRecurringDays(post.recurringDays)}</strong> • A cada {formatIntervalText(post.intervalMinutes || 60)} ({post.intervalStart || '08:00'} às {post.intervalEnd || '22:00'})</span>
                                                            ) : (
                                                                <span><strong>{formatRecurringDays(post.recurringDays)}</strong> às {post.recurringTimes || '09:00'}</span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Destinos */}
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        {post.postarWhatsapp !== false && (
                                                            <span title="WhatsApp Grupos" style={{ display: 'flex', alignItems: 'center', background: '#f0fdf4', padding: '3px 6px', borderRadius: '4px', color: '#166534', fontWeight: 'bold' }}>
                                                                <WhatsappIcon size={12} color="#25D366" /> WA
                                                            </span>
                                                        )}
                                                        {post.postarStatus && (
                                                            <span title="WhatsApp Status" style={{ display: 'flex', alignItems: 'center', background: '#ecfdf5', padding: '3px 6px', borderRadius: '4px', color: '#047857', fontWeight: 'bold' }}>
                                                                <WhatsappIcon size={12} color="#10b981" /> Status
                                                            </span>
                                                        )}
                                                        {post.postarTelegram !== false && (
                                                            <span title="Telegram" style={{ display: 'flex', alignItems: 'center', background: '#f0f9ff', padding: '3px 6px', borderRadius: '4px', color: '#0369a1', fontWeight: 'bold' }}>
                                                                <TelegramIcon size={12} color="#0088cc" /> TG
                                                            </span>
                                                        )}
                                                        {post.postarInstagram && (
                                                            <span title={`Instagram (${post.postarInstagramType || 'both'})`} style={{ display: 'flex', alignItems: 'center', background: '#fdf2f8', padding: '3px 6px', borderRadius: '4px', color: '#be185d', fontWeight: 'bold' }}>
                                                                <InstagramIcon size={12} color="#e1306c" /> Insta
                                                            </span>
                                                        )}
                                                    </div>

                                                    {post.lastSentAt && (
                                                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                            Último disparo: {formatDateTimeBR(post.lastSentAt)}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Aviso de erro ou grupos ausentes */}
                                                {post.lastError && (
                                                    <div style={{
                                                        backgroundColor: '#fef2f2',
                                                        border: '1px solid #fecaca',
                                                        borderRadius: '8px',
                                                        padding: '8px 12px',
                                                        fontSize: '12px',
                                                        color: '#b91c1c',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        marginTop: '8px'
                                                    }}>
                                                        <span>⚠️ <strong>Aviso:</strong> {post.lastError}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Barra de Ações */}
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '12px', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                                            {/* Disparar Agora */}
                                            <button
                                                onClick={() => handleQuickSend(post.id, post.title)}
                                                disabled={quickSendingId === post.id}
                                                style={{
                                                    backgroundColor: '#3b82f6',
                                                    color: '#fff',
                                                    border: 'none',
                                                    padding: '8px 14px',
                                                    borderRadius: '8px',
                                                    fontWeight: '700',
                                                    fontSize: '12px',
                                                    cursor: quickSendingId === post.id ? 'not-allowed' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                {quickSendingId === post.id ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} fill="#fff" />}
                                                Disparar Agora
                                            </button>

                                            {/* Agendar / Configurar Horários */}
                                            <button
                                                onClick={() => handleOpenScheduleModal(post)}
                                                style={{
                                                    backgroundColor: '#fff',
                                                    color: '#334155',
                                                    border: '1px solid #cbd5e1',
                                                    padding: '8px 12px',
                                                    borderRadius: '8px',
                                                    fontWeight: '600',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <Clock size={13} /> Agendar
                                            </button>

                                            {/* Pausar / Ativar se for recorrente */}
                                            {isRecurring && (
                                                <button
                                                    onClick={() => handleTogglePostStatus(post)}
                                                    style={{
                                                        backgroundColor: isActive ? '#fff7ed' : '#f0fdf4',
                                                        color: isActive ? '#c2410c' : '#166534',
                                                        border: isActive ? '1px solid #fdba74' : '1px solid #86efac',
                                                        padding: '8px 12px',
                                                        borderRadius: '8px',
                                                        fontWeight: '600',
                                                        fontSize: '12px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    {isActive ? <Pause size={13} /> : <Play size={13} />}
                                                    {isActive ? 'Pausar' : 'Ativar'}
                                                </button>
                                            )}

                                            {/* Usar no Formulário */}
                                            <button
                                                onClick={() => handleSelectSavedPost(post)}
                                                style={{
                                                    backgroundColor: '#f1f5f9',
                                                    color: '#475569',
                                                    border: 'none',
                                                    padding: '8px 12px',
                                                    borderRadius: '8px',
                                                    fontWeight: '600',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <Edit3 size={13} /> Editar
                                            </button>

                                            {/* Excluir */}
                                            <button
                                                onClick={() => handleDeleteSavedPost(post.id)}
                                                style={{
                                                    backgroundColor: '#fef2f2',
                                                    color: '#ef4444',
                                                    border: 'none',
                                                    padding: '8px 12px',
                                                    borderRadius: '8px',
                                                    fontWeight: '600',
                                                    fontSize: '12px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* MODAL DE AGENDAMENTO */}
                {scheduleModalOpen && selectedPostForSchedule && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.65)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: '20px'
                    }}>
                        <div style={{
                            backgroundColor: '#fff',
                            borderRadius: '20px',
                            maxWidth: '600px',
                            width: '100%',
                            padding: '30px',
                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            textAlign: 'left'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '14px' }}>
                                <div>
                                    <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px', fontWeight: '800' }}>
                                        ⚙️ Configurar Horários & Disparo
                                    </h3>
                                    <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                                        {selectedPostForSchedule.title}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setScheduleModalOpen(false)}
                                    style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                {/* Modo de Agendamento */}
                                <div>
                                    <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '10px' }}>
                                        Tipo de Agendamento:
                                    </label>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setModalScheduleType('now')}
                                            style={{
                                                padding: '10px',
                                                borderRadius: '10px',
                                                border: modalScheduleType === 'now' ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                                                backgroundColor: modalScheduleType === 'now' ? '#eff6ff' : '#fff',
                                                color: modalScheduleType === 'now' ? '#1d4ed8' : '#64748b',
                                                fontWeight: '700',
                                                fontSize: '12px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            ⚡ Imediato / Manual
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setModalScheduleType('once')}
                                            style={{
                                                padding: '10px',
                                                borderRadius: '10px',
                                                border: modalScheduleType === 'once' ? '2px solid #8b5cf6' : '1px solid #cbd5e1',
                                                backgroundColor: modalScheduleType === 'once' ? '#f5f3ff' : '#fff',
                                                color: modalScheduleType === 'once' ? '#6d28d9' : '#64748b',
                                                fontWeight: '700',
                                                fontSize: '12px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            📅 Uma vez só
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setModalScheduleType('recurring')}
                                            style={{
                                                padding: '10px',
                                                borderRadius: '10px',
                                                border: modalScheduleType === 'recurring' ? '2px solid #10b981' : '1px solid #cbd5e1',
                                                backgroundColor: modalScheduleType === 'recurring' ? '#ecfdf5' : '#fff',
                                                color: modalScheduleType === 'recurring' ? '#047857' : '#64748b',
                                                fontWeight: '700',
                                                fontSize: '12px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            🔁 Recorrente
                                        </button>
                                    </div>
                                </div>

                                {/* Campo Data para "Uma vez só" */}
                                {modalScheduleType === 'once' && (
                                    <div>
                                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '8px' }}>
                                            Data e Hora de Disparo:
                                        </label>
                                        <input
                                            type="datetime-local"
                                            value={modalScheduledAt}
                                            onChange={(e) => setModalScheduledAt(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '10px 14px',
                                                borderRadius: '8px',
                                                border: '2px solid #cbd5e1',
                                                fontSize: '14px',
                                                outline: 'none',
                                                fontWeight: '600',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Campos para "Recorrente" */}
                                {modalScheduleType === 'recurring' && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                        <div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155' }}>
                                                    Dias da Semana:
                                                </label>
                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setModalRecurringDays([0, 1, 2, 3, 4, 5, 6])}
                                                        style={{ fontSize: '11px', background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', color: '#475569' }}
                                                    >
                                                        Todos os dias
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setModalRecurringDays([1, 2, 3, 4, 5])}
                                                        style={{ fontSize: '11px', background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', color: '#475569' }}
                                                    >
                                                        Seg a Sex
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setModalRecurringDays([0, 6])}
                                                        style={{ fontSize: '11px', background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', color: '#475569' }}
                                                    >
                                                        Fim de semana
                                                    </button>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                {DIAS_SEMANA_LIST.map((dia) => {
                                                    const selected = modalRecurringDays.includes(dia.id);
                                                    return (
                                                        <button
                                                            key={dia.id}
                                                            type="button"
                                                            onClick={() => {
                                                                if (selected) {
                                                                    setModalRecurringDays(modalRecurringDays.filter(d => d !== dia.id));
                                                                } else {
                                                                    setModalRecurringDays([...modalRecurringDays, dia.id]);
                                                                }
                                                            }}
                                                            style={{
                                                                padding: '6px 12px',
                                                                borderRadius: '8px',
                                                                border: selected ? '2px solid #10b981' : '1px solid #cbd5e1',
                                                                backgroundColor: selected ? '#10b981' : '#fff',
                                                                color: selected ? '#fff' : '#64748b',
                                                                fontWeight: 'bold',
                                                                fontSize: '12px',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            {dia.nome}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Alternador de Modo de Programação no Modal */}
                                        <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '10px' }}>
                                            <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '8px' }}>
                                                Modo de Programação:
                                            </label>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setModalRecurringMode('fixed_times')}
                                                    style={{
                                                        padding: '8px',
                                                        borderRadius: '8px',
                                                        border: modalRecurringMode === 'fixed_times' ? '2px solid #10b981' : '1px solid #cbd5e1',
                                                        backgroundColor: modalRecurringMode === 'fixed_times' ? '#ecfdf5' : '#fff',
                                                        color: modalRecurringMode === 'fixed_times' ? '#047857' : '#64748b',
                                                        fontWeight: '700',
                                                        fontSize: '12px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Horários Específicos
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setModalRecurringMode('interval')}
                                                    style={{
                                                        padding: '8px',
                                                        borderRadius: '8px',
                                                        border: modalRecurringMode === 'interval' ? '2px solid #10b981' : '1px solid #cbd5e1',
                                                        backgroundColor: modalRecurringMode === 'interval' ? '#ecfdf5' : '#fff',
                                                        color: modalRecurringMode === 'interval' ? '#047857' : '#64748b',
                                                        fontWeight: '700',
                                                        fontSize: '12px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Intervalo de Tempo
                                                </button>
                                            </div>

                                            {modalRecurringMode === 'fixed_times' ? (
                                                <div>
                                                    <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '6px' }}>
                                                        Horários de Disparo (separados por vírgula):
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={modalRecurringTimes}
                                                        onChange={(e) => setModalRecurringTimes(e.target.value)}
                                                        placeholder="Ex: 09:00, 14:00, 18:00"
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px 14px',
                                                            borderRadius: '8px',
                                                            border: '2px solid #cbd5e1',
                                                            fontSize: '14px',
                                                            outline: 'none',
                                                            fontWeight: '600',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                    <div>
                                                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '4px' }}>
                                                            Frequência / Intervalo:
                                                        </label>
                                                        <select
                                                            value={modalIntervalMinutes}
                                                            onChange={(e) => setModalIntervalMinutes(parseInt(e.target.value, 10))}
                                                            style={{
                                                                width: '100%',
                                                                padding: '10px',
                                                                borderRadius: '8px',
                                                                border: '2px solid #cbd5e1',
                                                                fontSize: '14px',
                                                                fontWeight: '600',
                                                                outline: 'none',
                                                                backgroundColor: '#fff',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        >
                                                            {INTERVALO_OPCOES.map(opt => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                        <div>
                                                            <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '2px' }}>Início:</span>
                                                            <input
                                                                type="time"
                                                                value={modalIntervalStart}
                                                                onChange={(e) => setModalIntervalStart(e.target.value)}
                                                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: '600', boxSizing: 'border-box' }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '2px' }}>Fim:</span>
                                                            <input
                                                                type="time"
                                                                value={modalIntervalEnd}
                                                                onChange={(e) => setModalIntervalEnd(e.target.value)}
                                                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: '600', boxSizing: 'border-box' }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Canais de Envio */}
                                <div>
                                    <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '10px' }}>
                                        Canais de Destino:
                                    </label>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setModalPostarWa(!modalPostarWa)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '8px 12px',
                                                borderRadius: '8px',
                                                border: modalPostarWa ? '2px solid #25D366' : '1px solid #cbd5e1',
                                                backgroundColor: modalPostarWa ? '#f0fdf4' : '#fff',
                                                color: modalPostarWa ? '#166534' : '#64748b',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <WhatsappIcon size={14} color={modalPostarWa ? "#25D366" : "#94a3b8"} /> WA Grupos
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => setModalPostarStatus(!modalPostarStatus)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '8px 12px',
                                                borderRadius: '8px',
                                                border: modalPostarStatus ? '2px solid #10b981' : '1px solid #cbd5e1',
                                                backgroundColor: modalPostarStatus ? '#ecfdf5' : '#fff',
                                                color: modalPostarStatus ? '#047857' : '#64748b',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <WhatsappIcon size={14} color={modalPostarStatus ? "#10b981" : "#94a3b8"} /> WA Status
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => setModalPostarTg(!modalPostarTg)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '8px 12px',
                                                borderRadius: '8px',
                                                border: modalPostarTg ? '2px solid #0088cc' : '1px solid #cbd5e1',
                                                backgroundColor: modalPostarTg ? '#f0f9ff' : '#fff',
                                                color: modalPostarTg ? '#0369a1' : '#64748b',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <TelegramIcon size={14} color={modalPostarTg ? "#0088cc" : "#94a3b8"} /> Telegram
                                        </button>

                                        {canInstagram && (
                                            <button
                                                type="button"
                                                onClick={() => setModalPostarIg(!modalPostarIg)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '8px 12px',
                                                    borderRadius: '8px',
                                                    border: modalPostarIg ? '2px solid #e1306c' : '1px solid #cbd5e1',
                                                    backgroundColor: modalPostarIg ? '#fdf2f8' : '#fff',
                                                    color: modalPostarIg ? '#be185d' : '#64748b',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <InstagramIcon size={14} color={modalPostarIg ? "#e1306c" : "#94a3b8"} /> Instagram
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Status do Agendamento */}
                                <div>
                                    <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '8px' }}>
                                        Status:
                                    </label>
                                    <select
                                        value={modalStatus}
                                        onChange={(e) => setModalStatus(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '10px 14px',
                                            borderRadius: '8px',
                                            border: '2px solid #cbd5e1',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            outline: 'none'
                                        }}
                                    >
                                        <option value="active">🟢 Ativo (Executar no agendamento)</option>
                                        <option value="paused">⏸️ Pausado (Não executar)</option>
                                        <option value="completed">✅ Concluído</option>
                                    </select>
                                </div>

                                {/* Botões do Modal */}
                                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setScheduleModalOpen(false)}
                                        style={{
                                            flex: 1,
                                            padding: '12px',
                                            borderRadius: '10px',
                                            border: '1px solid #cbd5e1',
                                            backgroundColor: '#f8fafc',
                                            color: '#64748b',
                                            fontWeight: '700',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSaveScheduleModal}
                                        disabled={isSavingScheduleModal}
                                        style={{
                                            flex: 2,
                                            padding: '12px',
                                            borderRadius: '10px',
                                            border: 'none',
                                            backgroundColor: '#10b981',
                                            color: '#fff',
                                            fontWeight: '800',
                                            cursor: isSavingScheduleModal ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {isSavingScheduleModal ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                        Salvar Configurações
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (userPlan === 'free' || userPlan === 'free_divulgador') {
        const hasEligiblePlatform = isShopee || isMagalu;
        return (
            <div className="page-wrapper" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <h1 style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    🏠 Home
                </h1>
                
                {mainTab === 'afiliado' ? (
                    <>
                        <p style={{ color: '#64748b', fontSize: '15px', marginBottom: '30px' }}>
                            Bem-vindo à sua Home! No <strong>Plano Gratuito</strong>, você pode utilizar 1 grupo ativo, realizar 1 disparo manual a cada 2 horas e configurar apenas 1 plataforma de afiliado ativa por mês. O disparo automático fica desativado nesta modalidade.
                        </p>

                {/* PREMIUM UPGRADE BANNER */}
                <div style={{
                    background: 'linear-gradient(135deg, #1e1b4b 0%, #311042 100%)',
                    borderRadius: '20px',
                    padding: '30px',
                    color: '#ffffff',
                    boxShadow: '0 10px 30px rgba(49, 16, 66, 0.15)',
                    marginBottom: '35px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    textAlign: 'left'
                }}>
                    <div style={{
                        position: 'absolute',
                        top: '-100px',
                        right: '-100px',
                        width: '300px',
                        height: '300px',
                        background: 'radial-gradient(circle, rgba(238, 77, 45, 0.15) 0%, rgba(0,0,0,0) 70%)',
                        zIndex: 1,
                        pointerEvents: 'none'
                    }} />
                    <div style={{
                        position: 'absolute',
                        bottom: '-100px',
                        left: '-100px',
                        width: '300px',
                        height: '300px',
                        background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, rgba(0,0,0,0) 70%)',
                        zIndex: 1,
                        pointerEvents: 'none'
                    }} />

                    <div style={{ position: 'relative', zIndex: 2 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}>
                            <div style={{ flex: '1 1 500px' }}>
                                <span style={{
                                    backgroundColor: 'rgba(238, 77, 45, 0.2)',
                                    color: '#ff7a59',
                                    padding: '6px 12px',
                                    borderRadius: '30px',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    display: 'inline-block',
                                    marginBottom: '12px'
                                }}>
                                    Comparativo de Planos 🚀
                                </span>
                                <h2 style={{ fontSize: '24px', fontWeight: '800', margin: '0 0 10px 0', letterSpacing: '-0.025em', color: '#fff' }}>
                                    Desbloqueie o Poder Total das Vendas Automáticas
                                </h2>
                                <p style={{ color: '#cbd5e1', fontSize: '14px', margin: '0 0 5px 0', lineHeight: '1.5' }}>
                                    O Plano Gratuito serve para você testar a nossa tecnologia. Ao migrar para um plano pago, você elimina limites de tempo, grupos e plataformas.
                                </p>
                            </div>
                            <div>
                                <button
                                    onClick={() => onNavigateTab && onNavigateTab('billing')}
                                    style={{
                                        background: 'linear-gradient(90deg, #ee4d2d 0%, #ff7a59 100%)',
                                        color: 'white',
                                        border: 'none',
                                        padding: '14px 28px',
                                        borderRadius: '12px',
                                        fontWeight: '800',
                                        fontSize: '15px',
                                        cursor: 'pointer',
                                        boxShadow: '0 10px 20px rgba(238, 77, 45, 0.3)',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'scale(1.03)';
                                        e.target.style.boxShadow = '0 12px 24px rgba(238, 77, 45, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'scale(1)';
                                        e.target.style.boxShadow = '0 10px 20px rgba(238, 77, 45, 0.3)';
                                    }}
                                >
                                    Fazer Upgrade Agora ✨
                                </button>
                            </div>
                        </div>

                        {/* Comparativo */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                            gap: '20px',
                            marginTop: '25px',
                            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                            paddingTop: '25px'
                        }}>
                            {/* Grátis */}
                            <div style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                padding: '20px',
                                borderRadius: '16px',
                                border: '1px solid rgba(255, 255, 255, 0.05)'
                            }}>
                                <h4 style={{ margin: '0 0 12px 0', color: '#94a3b8', fontSize: '15px', fontWeight: '800' }}>Plano Gratuito</h4>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>❌ Disparo Automático Inativo</li>
                                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>❌ Limite de 1 Grupo ativo</li>
                                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>❌ 1 Plataforma ativa (Troca 1x/mês)</li>
                                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>❌ Envio manual (cooldown de 2h)</li>
                                </ul>
                            </div>

                            {/* Básico */}
                            <div style={{
                                backgroundColor: 'rgba(238, 77, 45, 0.05)',
                                padding: '20px',
                                borderRadius: '16px',
                                border: '1px solid rgba(238, 77, 45, 0.15)'
                            }}>
                                <h4 style={{ margin: '0 0 12px 0', color: '#ff7a59', fontSize: '15px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    Plano Básico <span style={{ fontSize: '10px', backgroundColor: '#ee4d2d', color: '#fff', padding: '2px 6px', borderRadius: '10px' }}>POPULAR</span>
                                </h4>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>✅ Disparo Automático Ativo</li>
                                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>✅ Até 5 Grupos simultâneos</li>
                                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>✅ Multi-plataformas (Todas ativas)</li>
                                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>✅ Envio manual sem cooldown</li>
                                </ul>
                            </div>

                            {/* Premium */}
                            <div style={{
                                backgroundColor: 'rgba(139, 92, 246, 0.05)',
                                padding: '20px',
                                borderRadius: '16px',
                                border: '1px solid rgba(139, 92, 246, 0.15)'
                            }}>
                                <h4 style={{ margin: '0 0 12px 0', color: '#a78bfa', fontSize: '15px', fontWeight: '800' }}>Plano Premium</h4>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>✅ Grupos e Canais Ilimitados</li>
                                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>✅ Autopost no Instagram (Stories/Feed)</li>
                                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>✅ Respostas de Direct Automáticas (IA)</li>
                                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>✅ Suporte VIP 24h e Recursos Exclusivos</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {hasEligiblePlatform ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '30px', alignItems: 'stretch' }}>
                        {renderConverterAndTemplate()}

                        {/* DISPAROS GRATUITOS */}

                        {/* DISPAROS GRATUITOS */}
                        <div style={{
                            background: 'white',
                            padding: '30px',
                            borderRadius: '20px',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            marginTop: '0px'
                        }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                                    <div style={{ backgroundColor: '#ecfdf5', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Send size={24} color="#10b981" />
                                    </div>
                                    <div style={{ textAlign: 'left' }}>
                                        <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px', fontWeight: '800' }}>🚀 Disparo Gratuito (1x a cada 2h)</h3>
                                        <p style={{ margin: 0, color: '#64748b', fontSize: '12px' }}>
                                            {isShopee
                                                ? "Escolha entre disparo automático ou envio de links da Shopee para seu WhatsApp/Telegram configurado."
                                                : "Envie links de ofertas da Magalu para seu WhatsApp/Telegram configurado."
                                            }
                                        </p>
                                    </div>
                                </div>

                                {/* TABS SELECTOR */}
                                {isShopee && (
                                    <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                                        <button
                                            onClick={() => setFreeTab("shopee")}
                                            style={{
                                                flex: 1,
                                                padding: '10px',
                                                borderRadius: '8px',
                                                border: 'none',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                backgroundColor: freeTab === 'shopee' ? '#ff4d2d' : '#f1f5f9',
                                                color: freeTab === 'shopee' ? 'white' : '#64748b',
                                                transition: '0.2s'
                                            }}
                                        >
                                            Shopee Automático
                                        </button>
                                        <button
                                            onClick={() => setFreeTab("manual")}
                                            style={{
                                                flex: 1,
                                                padding: '10px',
                                                borderRadius: '8px',
                                                border: 'none',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                backgroundColor: freeTab === 'manual' ? '#ff4d2d' : '#f1f5f9',
                                                color: freeTab === 'manual' ? 'white' : '#64748b',
                                                transition: '0.2s'
                                            }}
                                        >
                                            Link Shopee
                                        </button>
                                    </div>
                                )}

                                {/* STATUS BADGE AND TARGET DETAILS */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '15px' }}>
                                    {(userConfig?.userWhatsappVerified && userConfig?.userWhatsappNumber) || whatsConnected ? (
                                        <div style={{ backgroundColor: '#ecfdf5', color: '#047857', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' }}>
                                            📡 WhatsApp: {whatsConnected ? 'Conectado e Ativo' : 'Configurado'}
                                        </div>
                                    ) : (
                                        <div style={{ backgroundColor: '#fef2f2', color: '#b91c1c', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' }}>
                                            ⚠️ WhatsApp: Não conectado (Vá em Configurações)
                                        </div>
                                    )}
                                    {userConfig?.telegramToken ? (
                                        <div style={{ backgroundColor: '#ecfdf5', color: '#047857', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' }}>
                                            📡 Telegram: Ativo
                                        </div>
                                    ) : (
                                        <div style={{ backgroundColor: '#fef2f2', color: '#b91c1c', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' }}>
                                            ⚠️ Telegram: Não configurado
                                        </div>
                                    )}
                                </div>

                                {/* DYNAMIC FORM BASED ON SELECTED TAB */}
                                {freeTab === 'shopee' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                        <p style={{ color: '#475569', fontSize: '13px', lineHeight: '1.6', margin: 0, textAlign: 'left' }}>
                                            O bot buscará de forma inteligente a melhor oferta do momento na Shopee, aplicará o seu modelo de mensagem e a enviará de forma automática com imagem e texto formatado.
                                        </p>

                                        <button
                                            onClick={handleDispararShopeeFree}
                                            disabled={sendingShopeeFree || !!cooldownTimeRemaining}
                                            style={{
                                                backgroundColor: sendingShopeeFree || !!cooldownTimeRemaining ? '#94a3b8' : '#ff4d2d',
                                                color: 'white',
                                                border: 'none',
                                                padding: '16px',
                                                borderRadius: '12px',
                                                fontWeight: '800',
                                                fontSize: '14px',
                                                cursor: sendingShopeeFree || !!cooldownTimeRemaining ? 'not-allowed' : 'pointer',
                                                transition: '0.2s',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                boxShadow: sendingShopeeFree || !!cooldownTimeRemaining ? 'none' : '0 4px 12px rgba(255, 77, 45, 0.2)'
                                            }}
                                        >
                                            {sendingShopeeFree ? (
                                                <>
                                                    <Loader2 size={18} className="animate-spin" />
                                                    BUSCANDO E DISPARANDO...
                                                </>
                                            ) : cooldownTimeRemaining ? (
                                                <>
                                                    🔒 PRÓXIMO DISPARO EM: {cooldownTimeRemaining}
                                                </>
                                            ) : (
                                                <>
                                                    <Send size={16} />
                                                    DISPARAR OFERTA DO DIA AGORA
                                                </>
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                        <p style={{ color: '#475569', fontSize: '13px', lineHeight: '1.6', margin: 0, textAlign: 'left' }}>
                                            {isMagalu
                                                ? "Cole um link de qualquer produto da Magalu. O bot obterá os detalhes da oferta e a enviará formatada para o seu canal."
                                                : "Cole um link de qualquer produto da Shopee. O bot obterá os detalhes da oferta e a enviará formatada para o seu canal."
                                            }
                                        </p>

                                        <input
                                            type="text"
                                            placeholder={isMagalu ? "Cole o link da Magalu aqui... Ex: magazineluiza.com.br/..." : "Cole o link da Shopee aqui... Ex: shopee.com.br/..."}
                                            value={manualLink}
                                            onChange={(e) => setManualLink(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '14px 16px',
                                                borderRadius: '10px',
                                                border: '2px solid #e2e8f0',
                                                fontSize: '14px',
                                                outline: 'none',
                                                boxSizing: 'border-box'
                                            }}
                                        />

                                        <button
                                            onClick={handleSendManualLink}
                                            disabled={isProcessing || !!cooldownTimeRemaining}
                                            style={{
                                                backgroundColor: isProcessing || !!cooldownTimeRemaining ? '#94a3b8' : (isMagalu ? '#0086ff' : '#ff4d2d'),
                                                color: 'white',
                                                border: 'none',
                                                padding: '16px',
                                                borderRadius: '12px',
                                                fontWeight: '800',
                                                fontSize: '14px',
                                                cursor: isProcessing || !!cooldownTimeRemaining ? 'not-allowed' : 'pointer',
                                                transition: '0.2s',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                boxShadow: isProcessing || !!cooldownTimeRemaining ? 'none' : (isMagalu ? '0 4px 12px rgba(0, 134, 255, 0.2)' : '0 4px 12px rgba(255, 77, 45, 0.2)')
                                            }}
                                        >
                                            {isProcessing ? (
                                                <>
                                                    <Loader2 size={18} className="animate-spin" />
                                                    ENVIANDO...
                                                </>
                                            ) : cooldownTimeRemaining ? (
                                                <>
                                                    🔒 PRÓXIMO ENVIADO EM: {cooldownTimeRemaining}
                                                </>
                                            ) : (
                                                <>
                                                    <Send size={16} />
                                                    ENVIAR OFERTA MANUAL AGORA
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    /* SETUP BLOCK FOR NONE OR INELIGIBLE ACTIVE PLATFORM */
                    <div style={{
                        background: 'white',
                        padding: '40px',
                        borderRadius: '24px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center',
                        maxWidth: '650px',
                        margin: '0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '20px'
                    }}>
                        <div style={{ backgroundColor: '#fff5f1', padding: '18px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Store size={40} color="#ee4d2d" />
                        </div>
                        <h3 style={{ margin: 0, color: '#1e293b', fontSize: '20px', fontWeight: '800' }}>
                            {activePlatform ? `⚠️ Plataforma Ativa Não Suportada no Plano Grátis` : `⚠️ Nenhuma Plataforma de Afiliado Ativa`}
                        </h3>
                        <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                            {activePlatform
                                ? `Sua plataforma ativa configurada atualmente é a ${activePlatform.toUpperCase()}. No Plano Gratuito, as ferramentas de conversão e envio de links no painel são exclusivas para as plataformas Shopee e Magazine Luiza.`
                                : `Para começar a testar as funcionalidades do robô no Plano Gratuito, você precisa ativar a Shopee ou a Magazine Luiza nas Configurações do painel.`
                            }
                        </p>
                        <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                            Defina sua plataforma ativa como <strong>Shopee</strong> ou <strong>Magazine Luiza</strong> na aba de <strong>Configurações</strong> para liberar as ferramentas de teste gratuítas (limite de 1 alteração por mês).
                        </p>
                        <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                            Caso queira enviar de outras plataformas como <strong>Mercado Livre, Amazon, Shein ou AliExpress</strong>, realize o upgrade para o <strong>Plano Básico</strong> para desbloquear todas as plataformas de forma simultânea e sem limites de tempo!
                        </p>
                        <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
                            <button
                                onClick={() => onNavigateTab && onNavigateTab('config')}
                                style={{
                                    backgroundColor: '#0f172a',
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '10px',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    transition: '0.2s'
                                }}
                            >
                                Configurar Plataforma ⚙️
                            </button>
                            <button
                                onClick={() => onNavigateTab && onNavigateTab('billing')}
                                style={{
                                    backgroundColor: '#ee4d2d',
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '10px',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    transition: '0.2s'
                                }}
                            >
                                Fazer Upgrade 🚀
                            </button>
                        </div>
                    </div>
                )}
                </>
                ) : (
                    renderDivulgadorPanel()
                )}
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <h1 style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                🏠 Home
            </h1>

            {mainTab === 'afiliado' ? (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '40px' }}>
                {stats.map((s, i) => (
                    <div key={i} style={{
                        background: 'white',
                        padding: '20px',
                        borderRadius: '15px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        borderLeft: `5px solid ${s.color}`
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: '500' }}>{s.label}</span>
                            {s.icon}
                        </div>
                        <span style={{ fontSize: '24px', fontWeight: 'bold' }}>{s.value}</span>
                    </div>
                ))}
            </div>

            {/* Seletor de postagem manual (WhatsApp e Instagram) */}
            <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                padding: '20px 25px',
                borderRadius: '16px',
                marginBottom: '30px',
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                maxWidth: '600px',
                margin: '0 auto 30px auto',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
            }}>
                {/* Toggle WhatsApp */}
                {whatsConnected && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                backgroundColor: '#25D366',
                                color: 'white',
                                padding: '8px',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <WhatsappIcon size={20} color="white" />
                            </div>
                            <div style={{ textAlign: 'left' }}>
                                <span style={{ fontWeight: '700', color: '#1e293b', display: 'block', fontSize: '14px' }}>Publicar no Whatsapp</span>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>
                                    {(userRole === 'admin' || userRole === 'moderator') ? 'Posta para todos os seus grupos de ofertas ativos' : 'Posta no seu WhatsApp pessoal configurado'}
                                </span>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={handleToggleWhatsapp}
                            style={{
                                position: 'relative',
                                width: '48px',
                                height: '24px',
                                backgroundColor: postarWhatsapp ? '#25D366' : '#cbd5e1',
                                borderRadius: '12px',
                                border: 'none',
                                cursor: 'pointer',
                                transition: '0.3s'
                            }}
                        >
                            <div style={{
                                width: '18px',
                                height: '18px',
                                backgroundColor: '#fff',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '3px',
                                left: postarWhatsapp ? '27px' : '3px',
                                transition: '0.3s',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }} />
                        </button>
                    </div>
                )}

                {whatsConnected && (
                    <>
                        <div style={{ height: '1px', backgroundColor: '#e2e8f0' }} />
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    backgroundColor: '#25D366',
                                    color: 'white',
                                    padding: '8px',
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <WhatsappIcon size={20} color="white" />
                                </div>
                                <div style={{ textAlign: 'left' }}>
                                    <span style={{ fontWeight: '700', color: '#1e293b', display: 'block', fontSize: '14px' }}>Publicar no Status</span>
                                    <span style={{ fontSize: '12px', color: '#64748b' }}>Posta a oferta no seu Status do WhatsApp</span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => handlePostarStatusChange(!postarStatus)}
                                style={{
                                    position: 'relative',
                                    width: '48px',
                                    height: '24px',
                                    backgroundColor: postarStatus ? '#25D366' : '#cbd5e1',
                                    borderRadius: '12px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: '0.3s'
                                }}
                            >
                                <div style={{
                                    width: '18px',
                                    height: '18px',
                                    backgroundColor: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '3px',
                                    left: postarStatus ? '27px' : '3px',
                                    transition: '0.3s',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }} />
                            </button>
                        </div>
                    </>
                )}

                {/* Linha Divisória & Toggle Telegram */}
                {userConfig?.telegramToken && (
                    <>
                        <div style={{ height: '1px', backgroundColor: '#e2e8f0' }} />
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    backgroundColor: '#0088cc',
                                    color: 'white',
                                    padding: '8px',
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <TelegramIcon size={18} color="white" />
                                </div>
                                <div style={{ textAlign: 'left' }}>
                                    <span style={{ fontWeight: '700', color: '#1e293b', display: 'block', fontSize: '14px' }}>Publicar no Telegram</span>
                                    <span style={{ fontSize: '12px', color: '#64748b' }}>Posta no seu bot ou canal do Telegram configurado</span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleToggleTelegram}
                                style={{
                                    position: 'relative',
                                    width: '48px',
                                    height: '24px',
                                    backgroundColor: postarTelegram ? '#0088cc' : '#cbd5e1',
                                    borderRadius: '12px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: '0.3s'
                                }}
                            >
                                <div style={{
                                    width: '18px',
                                    height: '18px',
                                    backgroundColor: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '3px',
                                    left: postarTelegram ? '27px' : '3px',
                                    transition: '0.3s',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }} />
                            </button>
                        </div>
                    </>
                )}

                {/* Linha Divisória & Toggle Instagram (Apenas Premium ou Admin) */}
                {(userPlan === 'premium' || userPlan === 'premium_divulgador' || userRole === 'admin') && userConfig?.instagramBusinessAccountId && (
                    <>
                        <div style={{ height: '1px', backgroundColor: '#e2e8f0' }} />
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    backgroundColor: '#e1306c',
                                    color: 'white',
                                    padding: '8px',
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <InstagramIcon size={20} color="white" />
                                </div>
                                <div style={{ textAlign: 'left' }}>
                                    <span style={{ fontWeight: '700', color: '#1e293b', display: 'block', fontSize: '14px' }}>Publicar no instagram</span>
                                    <span style={{ fontSize: '12px', color: '#64748b' }}>Posta no Feed e nos Stories (ignora o tempo de segurança)</span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleToggleInstagram}
                                style={{
                                    position: 'relative',
                                    width: '48px',
                                    height: '24px',
                                    backgroundColor: postarInstagram ? '#e1306c' : '#cbd5e1',
                                    borderRadius: '12px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: '0.3s'
                                }}
                            >
                                <div style={{
                                    width: '18px',
                                    height: '18px',
                                    backgroundColor: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '3px',
                                    left: postarInstagram ? '27px' : '3px',
                                    transition: '0.3s',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }} />
                            </button>
                        </div>
                        {postarInstagram && (
                            <div style={{ marginTop: '10px', marginLeft: '40px' }}>
                                <select
                                    value={postarInstagramType}
                                    onChange={(e) => handlePostarInstagramTypeChange(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '13px',
                                        color: '#475569',
                                        outline: 'none',
                                        backgroundColor: '#fff'
                                    }}
                                >
                                    <option value="both">Onde Postar: Ambos (Feed e Stories)</option>
                                    <option value="feed">Onde Postar: Apenas no Feed</option>
                                    <option value="story">Onde Postar: Apenas nos Stories</option>
                                </select>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Card de Programação de Horários & Modo Automático (Afiliado) */}
            <div style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '20px',
                padding: '28px',
                marginBottom: '35px',
                boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.05)',
                position: 'relative'
            }}>
                {/* Cabeçalho do Card */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '15px',
                    borderBottom: '1px solid #f1f5f9',
                    paddingBottom: '20px',
                    marginBottom: '25px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{
                            backgroundColor: schedAutoPostStatus ? '#ecfdf5' : '#f1f5f9',
                            color: schedAutoPostStatus ? '#10b981' : '#94a3b8',
                            padding: '12px',
                            borderRadius: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: '0.3s'
                        }}>
                            <Clock size={24} />
                        </div>
                        <div style={{ textAlign: 'left' }}>
                            <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                ⏰ Programação de Horários & Modo Automático
                            </h3>
                            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                                Configure os dias, horários e frequência de envio automático de ofertas
                            </p>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{
                            padding: '6px 14px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '800',
                            letterSpacing: '0.03em',
                            backgroundColor: schedAutoPostStatus ? '#ecfdf5' : '#fef2f2',
                            color: schedAutoPostStatus ? '#059669' : '#dc2626',
                            border: `1px solid ${schedAutoPostStatus ? '#a7f3d0' : '#fecaca'}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: schedAutoPostStatus ? '#10b981' : '#ef4444', display: 'inline-block' }} />
                            {schedAutoPostStatus ? "ROBÔ ATIVO" : "ROBÔ PAUSADO"}
                        </span>

                        {/* Switch de Ativação Geral */}
                        <button
                            type="button"
                            onClick={handleToggleAutoPostStatusImmediate}
                            style={{
                                position: 'relative',
                                width: '56px',
                                height: '30px',
                                borderRadius: '15px',
                                backgroundColor: schedAutoPostStatus ? '#22c55e' : '#cbd5e1',
                                border: 'none',
                                cursor: 'pointer',
                                transition: '0.3s',
                                outline: 'none'
                            }}
                            title={schedAutoPostStatus ? "Clique para pausar o robô automático" : "Clique para ativar o robô automático"}
                        >
                            <div style={{
                                width: '22px',
                                height: '22px',
                                backgroundColor: '#fff',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '4px',
                                left: schedAutoPostStatus ? '30px' : '4px',
                                transition: '0.3s',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }} />
                        </button>
                    </div>
                </div>

                {/* Corpo das Configurações de Horário */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    
                    {/* 1. Dias de Funcionamento */}
                    <div style={{ textAlign: 'left' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                            <label style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Calendar size={16} color="#3b82f6" /> Dias de Execução:
                            </label>
                            
                            {/* Atalhos Rápidos */}
                            <div style={{ display: 'flex', gap: '6px' }}>
                                <button
                                    type="button"
                                    onClick={() => setSchedDaysPreset('all')}
                                    style={{
                                        padding: '4px 10px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        borderRadius: '6px',
                                        border: '1px solid #cbd5e1',
                                        backgroundColor: schedDiasExecucao === '0,1,2,3,4,5,6' ? '#eff6ff' : '#fff',
                                        color: schedDiasExecucao === '0,1,2,3,4,5,6' ? '#2563eb' : '#64748b',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Todos os dias
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSchedDaysPreset('weekdays')}
                                    style={{
                                        padding: '4px 10px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        borderRadius: '6px',
                                        border: '1px solid #cbd5e1',
                                        backgroundColor: schedDiasExecucao === '1,2,3,4,5' ? '#eff6ff' : '#fff',
                                        color: schedDiasExecucao === '1,2,3,4,5' ? '#2563eb' : '#64748b',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Seg a Sex
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSchedDaysPreset('weekend')}
                                    style={{
                                        padding: '4px 10px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        borderRadius: '6px',
                                        border: '1px solid #cbd5e1',
                                        backgroundColor: (schedDiasExecucao === '0,6' || schedDiasExecucao === '6,0') ? '#eff6ff' : '#fff',
                                        color: (schedDiasExecucao === '0,6' || schedDiasExecucao === '6,0') ? '#2563eb' : '#64748b',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Fim de semana
                                </button>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {[
                                { id: 0, nome: 'Dom' }, { id: 1, nome: 'Seg' }, { id: 2, nome: 'Ter' },
                                { id: 3, nome: 'Qua' }, { id: 4, nome: 'Qui' }, { id: 5, nome: 'Sex' }, { id: 6, nome: 'Sáb' }
                            ].map(dia => {
                                const selected = schedDiasExecucao?.split(',').map(d => parseInt(d.trim(), 10)).includes(dia.id);
                                return (
                                    <button
                                        key={dia.id}
                                        type="button"
                                        onClick={() => toggleSchedDay(dia.id)}
                                        style={{
                                            padding: '8px 16px',
                                            borderRadius: '10px',
                                            border: selected ? '2px solid #22c55e' : '1px solid #e2e8f0',
                                            backgroundColor: selected ? '#f0fdf4' : '#fff',
                                            color: selected ? '#15803d' : '#64748b',
                                            fontWeight: '700',
                                            fontSize: '13px',
                                            cursor: 'pointer',
                                            transition: '0.2s',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        {selected && <Check size={14} />} {dia.nome}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* 2. Horários de Funcionamento (Início e Pausa) */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                        gap: '20px',
                        background: '#f8fafc',
                        padding: '18px',
                        borderRadius: '14px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <div>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                                <WhatsappIcon size={16} color="#25D366" /> Janela de Envio (WhatsApp / Telegram)
                            </span>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' }}>Início (Hora)</span>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="number"
                                            min="0"
                                            max="23"
                                            value={schedHoraInicio}
                                            onChange={(e) => setSchedHoraInicio(parseInt(e.target.value, 10))}
                                            style={{
                                                width: '100%',
                                                padding: '10px 30px 10px 12px',
                                                borderRadius: '8px',
                                                border: '1px solid #cbd5e1',
                                                fontSize: '14px',
                                                fontWeight: '700',
                                                outline: 'none',
                                                backgroundColor: '#fff',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <span style={{ position: 'absolute', right: '10px', top: '10px', fontSize: '12px', color: '#94a3b8', fontWeight: 'bold' }}>hs</span>
                                    </div>
                                </div>
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' }}>Pausa (Hora)</span>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="number"
                                            min="0"
                                            max="23"
                                            value={schedHoraFim}
                                            onChange={(e) => setSchedHoraFim(parseInt(e.target.value, 10))}
                                            style={{
                                                width: '100%',
                                                padding: '10px 30px 10px 12px',
                                                borderRadius: '8px',
                                                border: '1px solid #cbd5e1',
                                                fontSize: '14px',
                                                fontWeight: '700',
                                                outline: 'none',
                                                backgroundColor: '#fff',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <span style={{ position: 'absolute', right: '10px', top: '10px', fontSize: '12px', color: '#94a3b8', fontWeight: 'bold' }}>hs</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {(userPlan === 'premium' || userPlan === 'premium_divulgador' || userRole === 'admin') && userConfig?.instagramBusinessAccountId && (
                            <div>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#e1306c', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                                    <InstagramIcon size={16} color="#e1306c" /> Janela do Instagram (Feed / Stories)
                                </span>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    <div>
                                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' }}>Início (Hora)</span>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="number"
                                                min="0"
                                                max="23"
                                                value={schedInstagramHoraInicio}
                                                onChange={(e) => setSchedInstagramHoraInicio(parseInt(e.target.value, 10))}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px 30px 10px 12px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '14px',
                                                    fontWeight: '700',
                                                    outline: 'none',
                                                    backgroundColor: '#fff',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                            <span style={{ position: 'absolute', right: '10px', top: '10px', fontSize: '12px', color: '#94a3b8', fontWeight: 'bold' }}>hs</span>
                                        </div>
                                    </div>
                                    <div>
                                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' }}>Pausa (Hora)</span>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="number"
                                                min="0"
                                                max="23"
                                                value={schedInstagramHoraFim}
                                                onChange={(e) => setSchedInstagramHoraFim(parseInt(e.target.value, 10))}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px 30px 10px 12px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '14px',
                                                    fontWeight: '700',
                                                    outline: 'none',
                                                    backgroundColor: '#fff',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                            <span style={{ position: 'absolute', right: '10px', top: '10px', fontSize: '12px', color: '#94a3b8', fontWeight: 'bold' }}>hs</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 3. Frequência / Intervalo entre Ofertas */}
                    <div style={{ textAlign: 'left' }}>
                        <label style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                            <RefreshCw size={16} color="#6366f1" /> Frequência de Postagem (Intervalo entre ofertas):
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            {[
                                { label: '15 min', val: 15 },
                                { label: '30 min', val: 30 },
                                { label: '45 min', val: 45 },
                                { label: '1 hora', val: 60 },
                                { label: '2 horas', val: 120 },
                                { label: '4 horas', val: 240 }
                            ].map(item => (
                                <button
                                    key={item.val}
                                    type="button"
                                    onClick={() => setSchedIntervaloMinutos(item.val)}
                                    style={{
                                        padding: '7px 14px',
                                        borderRadius: '8px',
                                        border: schedIntervaloMinutos === item.val ? '2px solid #6366f1' : '1px solid #cbd5e1',
                                        backgroundColor: schedIntervaloMinutos === item.val ? '#eef2ff' : '#fff',
                                        color: schedIntervaloMinutos === item.val ? '#4338ca' : '#64748b',
                                        fontWeight: '700',
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        transition: '0.2s'
                                    }}
                                >
                                    {item.label}
                                </button>
                            ))}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
                                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>Personalizado:</span>
                                <input
                                    type="number"
                                    min="1"
                                    max="1440"
                                    value={schedIntervaloMinutos}
                                    onChange={(e) => setSchedIntervaloMinutos(parseInt(e.target.value, 10) || 15)}
                                    style={{
                                        width: '80px',
                                        padding: '7px 10px',
                                        borderRadius: '8px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '13px',
                                        fontWeight: '700',
                                        textAlign: 'center',
                                        outline: 'none'
                                    }}
                                />
                                <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>min</span>
                            </div>
                        </div>
                    </div>

                    {/* 4. Plataformas Habilitadas no Agendamento Automático */}
                    <div style={{ textAlign: 'left' }}>
                        <label style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                            <Store size={16} color="#ea580c" /> Plataformas Consultadas no Agendamento Automático:
                        </label>
                        <div className="platform-scroll-container">
                            {[
                                { id: 'shopee', nome: 'Shopee', color: '#ff4d2d', show: Boolean(userConfig?.shopeeAppId) },
                                { id: 'ml', nome: 'Mercado Livre', color: '#ffe600', textColor: '#000', show: Boolean(userConfig?.mlTag) },
                                { id: 'amazon', nome: 'Amazon', color: '#232f3e', show: Boolean(userConfig?.amazonTag) },
                                { id: 'magalu', nome: 'Magalu', color: '#0086ff', show: Boolean(userConfig?.magaluTag) },
                                { id: 'shein', nome: 'Shein', color: '#222', show: Boolean(userConfig?.sheinUid) },
                                { id: 'aliexpress', nome: 'AliExpress', color: '#ff4747', show: Boolean(userConfig?.aliexpressAppKey) },
                                { id: 'tiktok', nome: 'TikTok Shop', color: '#fe2c55', show: true }
                            ].filter(p => p.show).map(p => {
                                const ativo = schedPlataformasAutoPost?.split(',').map(x => x.trim().toLowerCase()).includes(p.id);
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => toggleSchedPlat(p.id)}
                                        style={{
                                            padding: '8px 16px',
                                            borderRadius: '10px',
                                            border: ativo ? `2px solid ${p.color}` : '2px solid #cbd5e1',
                                            cursor: 'pointer',
                                            backgroundColor: ativo ? p.color : '#fff',
                                            color: ativo ? (p.textColor || '#fff') : '#64748b',
                                            fontWeight: 'bold',
                                            fontSize: '13px',
                                            transition: '0.2s',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        {ativo ? '✓' : '+'} {p.nome}
                                    </button>
                                );
                            })}
                            {!userConfig?.shopeeAppId && !userConfig?.mlTag && !userConfig?.amazonTag && !userConfig?.magaluTag && !userConfig?.sheinUid && !userConfig?.aliexpressAppKey && (
                                <div style={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic', padding: '6px 0' }}>
                                    ⚙️ Nenhuma plataforma cadastrada. Adicione suas credenciais no menu <strong>Configurações</strong>.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 5. Destinos e Canais do Modo Automático */}
                    <div style={{ textAlign: 'left' }}>
                        <label style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                            <Send size={16} color="#0284c7" /> Destinos & Canais do Agendamento Automático:
                        </label>
                        <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#64748b' }}>
                            Defina exatamente para onde o robô automático deve enviar as ofertas agendadas.
                        </p>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                            gap: '12px'
                        }}>
                            {/* Switch WhatsApp Grupos */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 14px',
                                borderRadius: '12px',
                                border: schedAutoPostWhatsapp ? '1.5px solid #86efac' : '1.5px solid #e2e8f0',
                                backgroundColor: schedAutoPostWhatsapp ? '#f0fdf4' : '#fafafa',
                                transition: '0.2s'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{
                                        backgroundColor: '#25D366',
                                        color: 'white',
                                        padding: '6px',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <WhatsappIcon size={18} color="white" />
                                    </div>
                                    <div>
                                        <span style={{ fontWeight: '700', color: '#1e293b', display: 'block', fontSize: '13px' }}>Grupos do WhatsApp</span>
                                        <span style={{ fontSize: '11px', color: '#64748b' }}>Postar nos grupos cadastrados</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setSchedAutoPostWhatsapp(!schedAutoPostWhatsapp)}
                                    style={{
                                        position: 'relative',
                                        width: '44px',
                                        height: '24px',
                                        borderRadius: '12px',
                                        backgroundColor: schedAutoPostWhatsapp ? '#25D366' : '#cbd5e1',
                                        border: 'none',
                                        cursor: 'pointer',
                                        transition: '0.3s'
                                    }}
                                >
                                    <div style={{
                                        width: '18px',
                                        height: '18px',
                                        backgroundColor: '#fff',
                                        borderRadius: '50%',
                                        position: 'absolute',
                                        top: '3px',
                                        left: schedAutoPostWhatsapp ? '23px' : '3px',
                                        transition: '0.3s',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }} />
                                </button>
                            </div>

                            {/* Switch Stories do WhatsApp (Status) */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 14px',
                                borderRadius: '12px',
                                border: schedAutoPostWhatsappStatus ? '1.5px solid #86efac' : '1.5px solid #e2e8f0',
                                backgroundColor: schedAutoPostWhatsappStatus ? '#f0fdf4' : '#fafafa',
                                transition: '0.2s'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{
                                        backgroundColor: '#128C7E',
                                        color: 'white',
                                        padding: '6px',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <WhatsappIcon size={18} color="white" />
                                    </div>
                                    <div>
                                        <span style={{ fontWeight: '700', color: '#1e293b', display: 'block', fontSize: '13px' }}>Stories / Status WhatsApp</span>
                                        <span style={{ fontSize: '11px', color: '#64748b' }}>Postar ofertas no seu Status/Stories</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setSchedAutoPostWhatsappStatus(!schedAutoPostWhatsappStatus)}
                                    style={{
                                        position: 'relative',
                                        width: '44px',
                                        height: '24px',
                                        borderRadius: '12px',
                                        backgroundColor: schedAutoPostWhatsappStatus ? '#128C7E' : '#cbd5e1',
                                        border: 'none',
                                        cursor: 'pointer',
                                        transition: '0.3s'
                                    }}
                                >
                                    <div style={{
                                        width: '18px',
                                        height: '18px',
                                        backgroundColor: '#fff',
                                        borderRadius: '50%',
                                        position: 'absolute',
                                        top: '3px',
                                        left: schedAutoPostWhatsappStatus ? '23px' : '3px',
                                        transition: '0.3s',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }} />
                                </button>
                            </div>

                            {/* Switch Stories do Instagram */}
                            {(userPlan === 'premium' || userPlan === 'premium_divulgador' || userRole === 'admin') && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '12px 14px',
                                    borderRadius: '12px',
                                    border: schedAutoPostInstagramStories ? '1.5px solid #fbcfe8' : '1.5px solid #e2e8f0',
                                    backgroundColor: schedAutoPostInstagramStories ? '#fdf2f8' : '#fafafa',
                                    transition: '0.2s'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{
                                            background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                                            color: 'white',
                                            padding: '6px',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <InstagramIcon size={18} color="white" />
                                        </div>
                                        <div>
                                            <span style={{ fontWeight: '700', color: '#1e293b', display: 'block', fontSize: '13px' }}>Stories do Instagram</span>
                                            <span style={{ fontSize: '11px', color: '#64748b' }}>Banner 9:16 com link nos Stories</span>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setSchedAutoPostInstagramStories(!schedAutoPostInstagramStories)}
                                        style={{
                                            position: 'relative',
                                            width: '44px',
                                            height: '24px',
                                            borderRadius: '12px',
                                            backgroundColor: schedAutoPostInstagramStories ? '#e1306c' : '#cbd5e1',
                                            border: 'none',
                                            cursor: 'pointer',
                                            transition: '0.3s'
                                        }}
                                    >
                                        <div style={{
                                            width: '18px',
                                            height: '18px',
                                            backgroundColor: '#fff',
                                            borderRadius: '50%',
                                            position: 'absolute',
                                            top: '3px',
                                            left: schedAutoPostInstagramStories ? '23px' : '3px',
                                            transition: '0.3s',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                        }} />
                                    </button>
                                </div>
                            )}

                            {/* Switch Feed do Instagram */}
                            {(userPlan === 'premium' || userPlan === 'premium_divulgador' || userRole === 'admin') && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '12px 14px',
                                    borderRadius: '12px',
                                    border: schedAutoPostInstagramFeed ? '1.5px solid #fbcfe8' : '1.5px solid #e2e8f0',
                                    backgroundColor: schedAutoPostInstagramFeed ? '#fdf2f8' : '#fafafa',
                                    transition: '0.2s'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{
                                            background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                                            color: 'white',
                                            padding: '6px',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <InstagramIcon size={18} color="white" />
                                        </div>
                                        <div>
                                            <span style={{ fontWeight: '700', color: '#1e293b', display: 'block', fontSize: '13px' }}>Feed do Instagram</span>
                                            <span style={{ fontSize: '11px', color: '#64748b' }}>Postar ofertas no Feed (com Direct)</span>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setSchedAutoPostInstagramFeed(!schedAutoPostInstagramFeed)}
                                        style={{
                                            position: 'relative',
                                            width: '44px',
                                            height: '24px',
                                            borderRadius: '12px',
                                            backgroundColor: schedAutoPostInstagramFeed ? '#e1306c' : '#cbd5e1',
                                            border: 'none',
                                            cursor: 'pointer',
                                            transition: '0.3s'
                                        }}
                                    >
                                        <div style={{
                                            width: '18px',
                                            height: '18px',
                                            backgroundColor: '#fff',
                                            borderRadius: '50%',
                                            position: 'absolute',
                                            top: '3px',
                                            left: schedAutoPostInstagramFeed ? '23px' : '3px',
                                            transition: '0.3s',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                        }} />
                                    </button>
                                </div>
                            )}

                            {/* Switch Telegram */}
                            {userConfig?.telegramToken && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '12px 14px',
                                    borderRadius: '12px',
                                    border: schedAutoPostTelegram ? '1.5px solid #93c5fd' : '1.5px solid #e2e8f0',
                                    backgroundColor: schedAutoPostTelegram ? '#eff6ff' : '#fafafa',
                                    transition: '0.2s'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{
                                            backgroundColor: '#0088cc',
                                            color: 'white',
                                            padding: '6px',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <TelegramIcon size={18} color="white" />
                                        </div>
                                        <div>
                                            <span style={{ fontWeight: '700', color: '#1e293b', display: 'block', fontSize: '13px' }}>Canal / Bot do Telegram</span>
                                            <span style={{ fontSize: '11px', color: '#64748b' }}>Postar no Telegram configurado</span>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setSchedAutoPostTelegram(!schedAutoPostTelegram)}
                                        style={{
                                            position: 'relative',
                                            width: '44px',
                                            height: '24px',
                                            borderRadius: '12px',
                                            backgroundColor: schedAutoPostTelegram ? '#0088cc' : '#cbd5e1',
                                            border: 'none',
                                            cursor: 'pointer',
                                            transition: '0.3s'
                                        }}
                                    >
                                        <div style={{
                                            width: '18px',
                                            height: '18px',
                                            backgroundColor: '#fff',
                                            borderRadius: '50%',
                                            position: 'absolute',
                                            top: '3px',
                                            left: schedAutoPostTelegram ? '23px' : '3px',
                                            transition: '0.3s',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                        }} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Botão de Salvar Programação */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                        <button
                            type="button"
                            onClick={handleSaveAffiliateSchedule}
                            disabled={savingAffiliateSchedule}
                            style={{
                                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                                color: 'white',
                                border: 'none',
                                padding: '12px 28px',
                                borderRadius: '12px',
                                fontWeight: '800',
                                fontSize: '14px',
                                cursor: savingAffiliateSchedule ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                                transition: '0.2s'
                            }}
                        >
                            {savingAffiliateSchedule ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                            Salvar Programação de Horários
                        </button>
                    </div>

                </div>
            </div>

            {/* Disparo em Massa */}
            <div className="page-container" style={{ textAlign: 'center', border: '2px dashed #e2e8f0' }}>
                <h3 style={{ color: '#1e293b', marginBottom: '10px' }}>🚀 Disparo em Massa</h3>
                <p style={{ color: '#64748b', marginBottom: '25px' }}>
                    {(userRole === 'admin' || userRole === 'moderator') ? 'Marque as plataformas e dispare para todos os grupos:' : 'Marque as plataformas e dispare para seus canais configurados (WhatsApp/Telegram):'}
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '25px' }}>
                    <div className="platform-scroll-container">
                        {userConfig?.shopeeAppId && (
                            <MultiSelectBtn
                                active={plataformasAtivas.includes('shopee')}
                                onClick={() => togglePlataforma('shopee')}
                                label="Shopee"
                                color="#ff4d2d"
                                icon={<ShoppingBag size={16} />}
                            />
                        )}
                        {userConfig?.mlTag && (
                            <MultiSelectBtn
                                active={plataformasAtivas.includes('ml')}
                                onClick={() => togglePlataforma('ml')}
                                label="M. Livre"
                                color="#ffe600"
                                textColor="#000"
                                icon={<Store size={16} />}
                            />
                        )}
                        {userConfig?.amazonTag && (
                            <MultiSelectBtn
                                active={plataformasAtivas.includes('amazon')}
                                onClick={() => togglePlataforma('amazon')}
                                label="Amazon"
                                color="#232f3e"
                                icon={<Handshake size={16} />}
                            />
                        )}
                        {userConfig?.magaluTag && (
                            <MultiSelectBtn
                                active={plataformasAtivas.includes('magalu')}
                                onClick={() => togglePlataforma('magalu')}
                                label="Magalu"
                                color="#0086ff"
                                icon={<ShoppingBag size={16} />}
                            />
                        )}
                        {userConfig?.sheinUid && (
                            <MultiSelectBtn
                                active={plataformasAtivas.includes('shein')}
                                onClick={() => togglePlataforma('shein')}
                                label="Shein"
                                color="#222"
                                icon={<ShoppingCart size={16} />}
                            />
                        )}
                        {userConfig?.aliexpressAppKey && (
                            <MultiSelectBtn
                                active={plataformasAtivas.includes('aliexpress')}
                                onClick={() => togglePlataforma('aliexpress')}
                                label="AliExpress"
                                color="#ff4747"
                                icon={<Package size={16} />}
                            />
                        )}
                        <MultiSelectBtn
                            active={plataformasAtivas.includes('tiktok')}
                            onClick={() => togglePlataforma('tiktok')}
                            label="TikTok Shop"
                            color="#fe2c55"
                            icon={<TikTokIcon size={16} color={plataformasAtivas.includes('tiktok') ? '#fff' : '#fe2c55'} />}
                        />
                        {!userConfig?.shopeeAppId && !userConfig?.mlTag && !userConfig?.amazonTag && !userConfig?.magaluTag && !userConfig?.sheinUid && !userConfig?.aliexpressAppKey && !userConfig?.tiktokCookie && !userConfig?.tiktokTag && !userConfig?.tiktokAccessToken && !userConfig?.tiktokPublisherId && (
                            <div style={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic', padding: '10px 0' }}>
                                ⚙️ Nenhuma plataforma configurada. Acesse as <strong>Configurações</strong> para adicionar suas contas de afiliado.
                            </div>
                        )}
                    </div>

                    <button
                        onClick={handleDisparoManual}
                        disabled={isProcessing}
                        style={{
                            backgroundColor: isProcessing ? '#94a3b8' : '#22c55e',
                            color: 'white',
                            border: 'none',
                            padding: '18px 60px',
                            borderRadius: '12px',
                            fontWeight: '800',
                            cursor: isProcessing ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            boxShadow: '0 10px 20px -5px rgba(34, 197, 94, 0.4)',
                            transition: '0.3s'
                        }}
                    >
                        {isProcessing ? <Loader2 size={22} className="animate-spin" /> : <Play size={22} fill="white" />}
                        {isProcessing ? 'PROCESSANDO...' : 'EXECUTAR DISPARO SELECIONADO'}
                    </button>
                </div>
            </div>

            {/* Publicação Personalizada */}
            <div className="page-container" style={{ textAlign: 'center', border: '2px dashed #e2e8f0', marginTop: '30px' }}>
                <h3 style={{ color: '#1e293b', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                    <Link size={22} color="#ee4d2d" /> Publicação Personalizada
                </h3>
                <p style={{ color: '#64748b', marginBottom: '25px' }}>Cole o link do produto para enviar agora:</p>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', maxWidth: '600px', margin: '0 auto' }}>
                    <input
                        type="text"
                        placeholder="Ex: https://amzn.to/... ou shopee..."
                        value={manualLink}
                        onChange={(e) => setManualLink(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '15px 20px',
                            borderRadius: '12px',
                            border: '2px solid #e2e8f0',
                            fontSize: '15px',
                            outline: 'none'
                        }}
                    />

                    <button
                        onClick={handleSendManualLink}
                        disabled={isProcessing}
                        style={{
                            backgroundColor: isProcessing ? '#94a3b8' : '#ee4d2d',
                            color: 'white',
                            border: 'none',
                            padding: '15px 40px',
                            borderRadius: '12px',
                            fontWeight: '800',
                            cursor: isProcessing ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '12px',
                            boxShadow: '0 10px 20px -5px rgba(238, 77, 45, 0.4)',
                            width: '100%'
                        }}
                    >
                        {isProcessing ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                        {isProcessing ? 'ENVIANDO...' : 'ENVIAR OFERTA MANUAL'}
                    </button>
                </div>
            </div>

            {/* Conversor de Link e Preview de Mensagem */}
            <div style={{ marginTop: '40px', borderTop: '1px solid #e2e8f0', paddingTop: '40px', textAlign: 'left' }}>
                <h3 style={{ color: '#1e293b', marginBottom: '20px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    ⚙️ Ferramenta de Conversão & Formatação
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '30px', alignItems: 'stretch' }}>
                    {renderConverterAndTemplate()}
                </div>
            </div>

            {/* BANCO DE OFERTAS TIKTOK SHOP (AUTOPOST & VITRINE) */}
            <TikTokOfferBank />
            </>
            ) : (
                renderDivulgadorPanel()
            )}
        </div>
    );
};

export default Dashboard;
